// src/components/BankPages/BankPages.jsx

import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PuffLoader from 'react-spinners/PuffLoader';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import UserService from '../../Services/user.service';
import EnumsService from '../../Services/enums.service';
import ExternalTransactionsService from '../../Services/externalTransaction.service';
import FinsiteService from '../../Services/finsite.service';
import NoDocumentsPdf from '../../assests/pdf/NoDocument.pdf';
import Constants from '../../assests/Constants/constants';
import BankAccountBar from '../../Components/BankMatching/BankAccountBar.jsx';
import BankPeriodPicker, {
    PERIOD_MODE,
    endOfUtcDay,
    startOfUtcDay,
    yearBounds,
} from '../../Components/BankMatching/BankPeriodPicker.jsx';
import { useBack } from "use-back";

import dayjs from 'dayjs';
import './BankPages.css';
import {
    TextField, InputAdornment, Tooltip, Switch, Button, IconButton,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
    Autocomplete, CircularProgress, Box, Typography, Chip, FormControlLabel,
} from '@mui/material';
import { beautifyNumber } from '../../Utils/FormatNumber';
import { handleDownloadTemplate } from '../../Utils/handleDownloadTemplate';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import LinkIcon from '@mui/icons-material/Link';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import AutocompleteSelect from '../../Components/Widgets/AutocompleteSelect/AutocompleteSelect';
import ImportDialog from '../../Components/BankMatching/ImportDialog';
import ImportPreviewModal from '../../Components/BankMatching/ImportPreviewModal.jsx';
import TransactionsTable from '../../Components/BankPages/SubComponents/TransactionsTable';
import ViewTransactionMatchModal from '../../Components/BankPages/SubComponents/ViewTransactionMatchModal';
// import EditExternalTransactionModal from './SubComponents/EditExternalTransactionModal';
import AddOrEditBankDialog from '../../Components/BankMatching/AddOrEditBankDialog'; // Assuming this component exists
import AddExternalTransactionDialog from '../../Components/BankMatching/AddExternalTransactionDialog';
import EditExternalTransactionDialog from '../../Components/BankPages/EditExternalTransaction';
import BankFile from "../../assests/templates/Xtram_Bank_Template.xlsx";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { NavHistoryContext } from '../../Contexts/NavHistoryContext';
import { useHamburgerMenu } from '../../Contexts/HamburgerMenuContext';
import { useClientNavigation } from '../../hooks/useClientNavigation.js';
import useBankWorkspaceLayout from '../../hooks/useBankWorkspaceLayout.js';
import DuplicateEntriesModal, {
    normalizeDate,
    normalizeAmount,
    normalizeReference,
} from '../../Components/DuplicateEntriesModal/DuplicateEntriesModal.jsx';
import * as XLSX from 'xlsx-js-style';
import { applyWorkbookLtr, buildReportWorkbook } from '../../Utils/ExcelReport';


const currencyOptions = Object
    .entries(Constants.ForeignCurrencyCode)
    .map(([code, num]) => ({ label: code, value: num }));

/** The API serializes this property as "cC4Digits"; accept the other casings too. */
const getCc4Digits = (o) => o?.cC4Digits ?? o?.cc4Digits ?? o?.CC4Digits ?? null;

/** Bank duplicate key — same rule as Excel / Finsite importers: date + reference + amount.
 *  Numeric references compare without leading zeros ("0123" == "123").
 *  When the modal enables last-N-digits mode, helpers.normalizeReference applies that. */
const bankDuplicateGetGroupKey = (row, helpers = {}) => {
    const normRef = helpers.normalizeReference || normalizeReference;
    return [
        normalizeDate(row.documentDate),
        normRef(row.reference),
        normalizeAmount(row.amount),
    ].join('|');
};

/**
 * Prefer deleting unmatched copies when a matched one exists in the same group; otherwise
 * keep the first row (same fallback as the journal modal).
 */
const bankShouldSelectForDeletion = (row, group, idx) => {
    const isMatched = (r) =>
        r.matchNumber != null && r.matchNumber !== -1 && r.matchNumber !== 0;
    const hasMatched = group.some(isMatched);
    if (hasMatched) return !isMatched(row);
    return idx !== 0;
};

/** Balances accumulate from here so they stay comparable to the bank's own. */
const BALANCE_EPOCH = new Date(Date.UTC(1970, 0, 1));

function BankPages() {
    const navigate = useNavigate();
    const location = useLocation();
    const { t, i18n } = useTranslation();
    const direction = i18n.dir();
    const { goBack } = useContext(NavHistoryContext);
    const { setMenuProps } = useHamburgerMenu();
    const [user, setUser] = useState(null);
    const [token, setToken] = useState('');
    const [client, setClient] = useState(null);
    const [loading, setLoading] = useState(false);
    const [type, setType] = useState('');
    const [processedCardsData, setProcessedCardsData] = useState([]);
    const [accountCardOptionsForBank, setAccountCardOptionsForBank] = useState([]);
    const [tBanksData, setTBanksData] = useState([]);
    const [accountCardsData, setAccountCardsData] = useState([]);
    const [accountCardEditDownloadOptions, setAccountCardEditDownloadOptions] =
        useState([]);
    const [foreignCurrencyCode, setForeignCurrencyCode] = useState(null);

    const [formattedPalCodeOptions, setFormattedPalCodeOptions] = useState([]);
    const [isClientDataFetched, setIsClientDataFetched] = useState(false);
    const [denseTableCreditDebitRows, setDenseTableCreditDebitRows] = useState([]);
    const [finsiteBalanceRows, setFinsiteBalanceRows] = useState([]);
    const [finsiteDiffRows, setFinsiteDiffRows] = useState([]);
    const [finsiteBalanceLoading, setFinsiteBalanceLoading] = useState(false);

    const [transactions, setTransactions] = useState([]);
    const [selectedTransactions, setSelectedTransactions] = useState([]);
    const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);
    const [selectedTransactionMatch, setSelectedTransactionMatch] = useState(null);
    const [isTransactionMatchModalOpen, setIsTransactionMatchModalOpen] = useState(false);
    const [isLoadingTransactionMatch, setIsLoadingTransactionMatch] = useState(false);
    const [openRemoveMatchDialog, setOpenRemoveMatchDialog] = useState(false);
    const [selectedMatchForRemoval, setSelectedMatchForRemoval] = useState(null);
    const [isRemovingMatch, setIsRemovingMatch] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showOnlyWithDocuments, setShowOnlyWithDocuments] = useState(false);
    const [selectedTBankIndex, setSelectedTBankIndex] = useState(-1); //used for dense table
    const [selectedTBank, setSelectedTBank] = useState("");
    const [startDate, setStartDate] = useState(() => {
        const stored = localStorage.getItem("StartDate");
        const date = stored ? new Date(stored) : null;
        const currentYear = new Date().getFullYear();
        const defaultStart = new Date(Date.UTC(currentYear, 0, 1));


        return date && !isNaN(date.getTime()) ? date : defaultStart;
    });
    const { viewBankTransactions } = useClientNavigation(client);
    const [endDate, setEndDate] = useState(() => {
        const stored = localStorage.getItem("EndDate");
        const date = stored ? new Date(stored) : null;
        const currentYear = new Date().getFullYear();
        const defaultEnd = new Date(Date.UTC(currentYear, 11, 31, 23, 59, 59));


        return date && !isNaN(date.getTime()) ? date : defaultEnd;
    });
    const [finsiteDialogOpen, setFinsiteDialogOpen] = useState(false);
    const [finsiteFrom, setFinsiteFrom] = useState(() => {
        const currentYear = new Date().getFullYear();
        return new Date(Date.UTC(currentYear, 0, 1)).toISOString().slice(0, 10);
    });
    const [finsiteTo, setFinsiteTo] = useState(() => new Date().toISOString().slice(0, 10));
    const [addBankTransactionDialog, setAddBankTransactionDialog] =
        useState(false);

    const [editBankTransactionDialog, setEditBankTransactionDialog] = useState(false);

    const [isMonthly, setIsMonthly] = useState(() => {
        const storedIsMonthly = localStorage.getItem('IsMonthly');
        return storedIsMonthly !== null ? JSON.parse(storedIsMonthly) : true;
    });

    // Explicit start/end period instead of "everything ever imported": without
    // it a 2026 view still listed rows going back to the first import.
    const [isRange, setIsRange] = useState(() => localStorage.getItem('IsRange') === 'true');
    const lastRangeRef = useRef({ start: null, end: null });
    const [focusMode, setFocusMode] = useBankWorkspaceLayout('BankPagesFocusMode');

    const [accountCardOptions, setAccountCardOptions] = useState([]);
    const [selectedAccountCard, setSelectedAccountCard] = useState('');
    const [finsiteCardIds, setFinsiteCardIds] = useState([]);
    const [finsiteMappedBookingAccountIds, setFinsiteMappedBookingAccountIds] = useState([]);
    const [finsiteHasCredential, setFinsiteHasCredential] = useState(false);
    const [finsiteLinkDialogOpen, setFinsiteLinkDialogOpen] = useState(false);
    const [finsiteAutoLinking, setFinsiteAutoLinking] = useState(false);
    const [finsiteDiscovering, setFinsiteDiscovering] = useState(false);
    const [finsiteSavingMapping, setFinsiteSavingMapping] = useState(false);
    const [finsiteCompanies, setFinsiteCompanies] = useState([]);
    const [finsiteAccounts, setFinsiteAccounts] = useState([]);
    const [finsiteBookingAccounts, setFinsiteBookingAccounts] = useState([]);
    const [finsiteLinkForm, setFinsiteLinkForm] = useState({
        companyEntryId: '',
        accountId: '',
        bookingAccountId: '',
    });
    // Accountant's request to join the Finsite service (mirrors the credit-terminal request flow).
    const [finsiteSignupRequest, setFinsiteSignupRequest] = useState(null);
    const [finsiteSignupDialogOpen, setFinsiteSignupDialogOpen] = useState(false);
    const [finsiteSignupForm, setFinsiteSignupForm] = useState({ accountantName: '', accountantId: '', email: '', phoneNumber: '' });
    const [finsiteSignupSending, setFinsiteSignupSending] = useState(false);

    useEffect(() => {
        setMenuProps({ client });
    }, [client, setMenuProps]);

    useEffect(() => {
        if (!endDate || isNaN(new Date(endDate).getTime())) {
            // Set endDate to December 31 of the current year
            setEndDate(new Date(new Date().getUTCFullYear(), 11, 31));
        }

        // Check if startDate is null, undefined, or an invalid date
        if (!startDate || isNaN(new Date(startDate).getTime())) {
            // Set startDate to January 1 of the current year
            setStartDate(new Date(new Date().getUTCFullYear(), 0, 2));
        }


    }, [endDate, startDate, setEndDate, setStartDate]);



    const [sortConfig, setSortConfig] = useState({
        key: 'transactionDate',
        direction: 'descending',
        type: 'date',
    });
    const [oldEditedTransaction, setOldEditedTransaction] = useState(null);
    const [editViewOnly, setEditViewOnly] = useState(false);
    const [editedTransaction, setEditedTransaction] = useState({
        caseId: '',
        accountCodeId: '',
        accountCodeName: '',
        description: '',
        debitAccount: 0,
        creditAccount: 0,
        reference: '',
        reference1: '',
        amount: '',
        transactionDate: ''
    });
    const [openImportDialog, setOpenImportDialog] = useState(false);
    const [openSumitImportDialog, setOpenSumitImportDialog] = useState(false);
    const [openHashavshevetImportDialog, setOpenHashavshevetImportDialog] = useState(false);
    const [openPreviewModal, setOpenPreviewModal] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [previewFile, setPreviewFile] = useState(null);
    const [isSumitImport, setIsSumitImport] = useState(false);
    const [isHashavshevetImport, setIsHashavshevetImport] = useState(false);
    const [oldBank, setOldBank] = useState(null);
    const bankOptions = Object.keys(Constants.BankNames).map((key) => ({
        value: key,
        label: `${Constants.BankNames[key]} - ${key}`, // Format: BankName - BankNumber
    }));
    const [denseTableRows, setDenseTableRows] = useState([]);
    const [mainCard, setMainCard] = useState("");
    const [selectedBankForCreate, setSelectedBankForCreate] = useState(null);
    const [editedBank, setEditedBank] = useState(false);
    const [bankBranch, setBankBranch] = useState("");
    const [bankAccountNumber, setBankAccountNumber] = useState("");
    const [cardForNewBank, setCardForNewBank] = useState(null);
    const [isEditingBank, setIsEditingBank] = useState(false);

    const denseTableHeader = useMemo(() => [
        t("BanksColCurrency"),
        t("BanksColBankNo"),
        t("BanksColBankName"),
        t("BanksColBranch"),
        t("BanksColAccount"),
        t("BanksColCard"),
        t("BanksColFinsite"),
    ], [t]);
    // Additional states for main card and dialogs
    const [mainCardOptions, setMainCardOptions] = useState([]);
    const [selectedMainCard, setSelectedMainCard] = useState(null);
    const [createBankModal, setCreateBankModal] = useState(false);
    const [selectedFormattedAccountCard, setSelectedFormattedAccountCard] = useState(null);
    // Fetch user and client data


    const selectedDenseTableRow = async (index) => {
        const tBank = tBanksData[index];
        if (!tBank) {
            return;
        }
        setSelectedTBank(tBank);
        setSelectedTBankIndex(index);
        setSelectedAccountCard(tBank.cardId);

        const cardIndexForAdv = processedCardsData.findIndex(
            (item) => item.cardId === tBank.cardId
        );

        if (cardIndexForAdv !== -1) {
            // If cardId is found
            const updatedMainCard = {
                ...processedCardsData[cardIndexForAdv],
                index: cardIndexForAdv,
            }; // Add the index to mainCard
            setMainCard(updatedMainCard); // Set the updated mainCard with the index
            setSelectedFormattedAccountCard(processedCardsData[cardIndexForAdv]);
        } else {
            // setMainCard(-1);
            // toast.info(t('NonBankAccountCardSelected'));
        }
    };


    useEffect(() => {

        if (mainCard) {


            // setSelectedAccountCard(mainCard.cardId);
            const cardIndex = tBanksData.findIndex(
                (item) => item.cardId?.toString() === mainCard.cardId.toString()
            );

            if (cardIndex !== -1) {
                setSelectedTBankIndex(cardIndex);
            } else {
                setSelectedTBankIndex(-1);
                // toast.info(t('NonBankAccountCardSelected'));
            }
        }
    }, [mainCard]);
    const handleFormattedAccountCardChange = (event, newValue) => {
        setMainCard(newValue);
        setSelectedAccountCard(newValue?.cardId);
        setSelectedFormattedAccountCard(newValue);
    };
    useEffect(() => {
        fetchUserData();
    }, []);

    const refreshFinsiteEligibility = async () => {
        if (!client?.caseId || !token) {
            setFinsiteCardIds([]);
            setFinsiteMappedBookingAccountIds([]);
            setFinsiteHasCredential(false);
            return;
        }
        try {
            const response = await FinsiteService.GetEligibility(client.caseId, token);
            if (response.ok) {
                const data = await response.json();
                setFinsiteCardIds((data?.cardIds || []).map((id) => String(id)));
                setFinsiteMappedBookingAccountIds(
                    (data?.mappedBookingAccountIds || []).map((id) => String(id))
                );
                setFinsiteHasCredential(!!data?.hasCredential);
            } else {
                setFinsiteCardIds([]);
                setFinsiteMappedBookingAccountIds([]);
                setFinsiteHasCredential(false);
            }
        } catch (error) {
            console.error('Failed to load Finsite eligibility:', error);
            setFinsiteCardIds([]);
            setFinsiteMappedBookingAccountIds([]);
            setFinsiteHasCredential(false);
        }
    };

    // Keep the banks table Finsite column in sync with linked cardIds from eligibility.
    useEffect(() => {
        if (!Array.isArray(tBanksData) || tBanksData.length === 0) {
            return;
        }
        const linkedSet = new Set((finsiteCardIds || []).map(String));
        const rows = tBanksData.map((bank) => {
            const isLinked = linkedSet.has(String(bank.cardId));
            return [
                (() => {
                    const currencyObj = currencyOptions.find(o => o.value === bank.foreignCurrencyCode);
                    const code = currencyObj?.label;
                    const symbol = Constants.ForeignCurrencySymbol[code] || '';
                    return code ? `${code}${symbol ? ' (' + symbol + ')' : ''}` : '-';
                })(),
                bank.bankCode || '-',
                bank.bankName || '-',
                bank.branchNumber || '-',
                bank.accountNumber || '-',
                bank.cardId || '-',
                isLinked ? (
                    <Chip
                        key={`finsite-${bank.cardId}`}
                        size="small"
                        icon={<CloudSyncIcon sx={{ fontSize: '14px !important' }} />}
                        label={t('FinsiteLinked')}
                        sx={{
                            height: 24,
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            bgcolor: 'rgba(13, 148, 136, 0.12)',
                            color: '#0F766E',
                            border: '1px solid rgba(13, 148, 136, 0.28)',
                            '& .MuiChip-icon': { color: '#0D9488' },
                            '& .MuiChip-label': { px: 0.75 },
                        }}
                    />
                ) : (
                    <Chip
                        key={`finsite-off-${bank.cardId}`}
                        size="small"
                        label={t('FinsiteNotLinked')}
                        variant="outlined"
                        sx={{
                            height: 24,
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            color: 'rgba(15, 23, 42, 0.45)',
                            borderColor: 'rgba(15, 23, 42, 0.12)',
                            bgcolor: 'rgba(15, 23, 42, 0.02)',
                            '& .MuiChip-label': { px: 0.75 },
                        }}
                    />
                ),
            ];
        });
        setDenseTableRows(rows);
    }, [tBanksData, finsiteCardIds, t]);

    // Load Finsite eligibility for this client so link/fetch actions show correctly.
    useEffect(() => {
        refreshFinsiteEligibility();
    }, [client, token]);

    // Latest Finsite signup request of the logged-in accountant (drives the join button + status chip).
    const refreshFinsiteSignupRequest = async () => {
        if (!token) return;
        try {
            const response = await FinsiteService.GetMySignupRequest(token);
            if (response.ok) {
                const data = await response.json();
                setFinsiteSignupRequest(data?.request || null);
            }
        } catch (error) {
            console.error('Failed to load Finsite signup request:', error);
        }
    };

    useEffect(() => {
        refreshFinsiteSignupRequest();
    }, [token]);

    const openFinsiteSignupDialog = () => {
        setFinsiteSignupForm({
            accountantName: user?.name || '',
            accountantId: user?.accountantId || client?.accountantId || '',
            email: user?.email || '',
            phoneNumber: user?.phoneNumber || '',
        });
        setFinsiteSignupDialogOpen(true);
    };

    const handleSendFinsiteSignupRequest = async () => {
        setFinsiteSignupSending(true);
        try {
            const response = await FinsiteService.CreateSignupRequest(
                {
                    accountantName: finsiteSignupForm.accountantName || null,
                    email: finsiteSignupForm.email || null,
                    phoneNumber: finsiteSignupForm.phoneNumber || null,
                },
                token
            );
            if (response.ok) {
                const data = await response.json();
                toast.success(
                    data?.alreadyExists
                        ? t('FinsiteSignupAlreadyRequested')
                        : t('FinsiteSignupRequestSent')
                );
                setFinsiteSignupDialogOpen(false);
                await refreshFinsiteSignupRequest();
            } else {
                toast.error(t('FinsiteSignupRequestFailed'));
            }
        } catch (error) {
            console.error('Failed to send Finsite signup request:', error);
            toast.error(t('FinsiteSignupRequestFailed'));
        } finally {
            setFinsiteSignupSending(false);
        }
    };

    // Status chip config for the accountant's Finsite signup request (0 Pending, 1 SentToFinsite, 2 Completed, 3 Rejected).
    const finsiteSignupStatusChip = (() => {
        if (finsiteSignupRequest == null) return null;
        switch (finsiteSignupRequest.status) {
            case 0:
                return { label: t('FinsiteSignupStatusPending'), color: '#ED8936' };
            case 1:
                return { label: t('FinsiteSignupStatusSent'), color: '#3182CE' };
            case 2:
                return { label: t('FinsiteSignupStatusCompleted'), color: '#28a745' };
            case 3:
                return { label: t('FinsiteSignupStatusRejected'), color: '#E53E3E' };
            case 4:
                return { label: t('FinsiteSignupStatusDisabled'), color: '#718096' };
            default:
                return null;
        }
    })();

    const isSelectedAccountFinsiteLinked = finsiteCardIds.includes(String(selectedAccountCard));
    const showFinsiteLinkActions = finsiteHasCredential && selectedTBank && !isSelectedAccountFinsiteLinked;

    // Load Finsite Balance / TotalCredit / TotalDebit for the linked account so we can
    // compare against the Xtram summary (same date range).
    const refreshFinsiteBalances = async (cardId, rangeStart, rangeEnd, xtramTotals) => {
        if (!client?.caseId || !cardId || !finsiteCardIds.includes(String(cardId))) {
            setFinsiteBalanceRows([]);
            setFinsiteDiffRows([]);
            return;
        }

        const toIso = (d) => {
            if (!d) return null;
            const dt = d instanceof Date ? d : new Date(d);
            if (Number.isNaN(dt.getTime())) return null;
            return dt.toISOString().slice(0, 10);
        };

        setFinsiteBalanceLoading(true);
        try {
            const response = await FinsiteService.GetCaseAccountBalances(
                client.caseId,
                cardId,
                toIso(rangeStart),
                toIso(rangeEnd),
                token
            );
            if (!response.ok) {
                setFinsiteBalanceRows([]);
                setFinsiteDiffRows([]);
                return;
            }
            const data = await response.json();
            if (!data?.success && data?.status === 'NoMapping') {
                setFinsiteBalanceRows([]);
                setFinsiteDiffRows([]);
                return;
            }

            // BankPages flips Credit↔Debit for display: Credit column = money-in (Finsite TotalCredit).
            const fBalance = Number(data.balance) || 0;
            const fCredit = Number(data.totalCredit) || 0;
            const fDebit = Number(data.totalDebit) || 0;

            setFinsiteBalanceRows([{
                balance: beautifyNumber(fBalance),
                credit: beautifyNumber(fCredit),
                debit: beautifyNumber(fDebit),
            }]);

            if (xtramTotals) {
                setFinsiteDiffRows([{
                    balance: beautifyNumber(fBalance - (Number(xtramTotals.balance) || 0)),
                    credit: beautifyNumber(fCredit - (Number(xtramTotals.credit) || 0)),
                    debit: beautifyNumber(fDebit - (Number(xtramTotals.debit) || 0)),
                }]);
            } else {
                setFinsiteDiffRows([]);
            }
        } catch (error) {
            console.error('Failed to load Finsite balances:', error);
            setFinsiteBalanceRows([]);
            setFinsiteDiffRows([]);
        } finally {
            setFinsiteBalanceLoading(false);
        }
    };
    // Offer joining Finsite when the accountant has no credential yet and no open request
    // (rejected or disabled subscriptions may re-apply).
    const showFinsiteSignupAction =
        !finsiteHasCredential &&
        (finsiteSignupRequest == null ||
            finsiteSignupRequest.status === 3 ||
            finsiteSignupRequest.status === 4);


    const handleOpenEditTBank = (index) => {
        const selectedBankData = tBanksData[index]; // Get the selected bank data from tBanksData

        setOldBank(selectedBankData);
        // Find the bank option that matches the selected bank's bankCode
        const selectedBank = bankOptions.find(
            (bank) => bank.value === selectedBankData.bankCode
        );

        setSelectedBankForCreate(selectedBank);
        setEditedBank(selectedBankData); // Set the entire bank data for editing

        // Set other values for editing
        setBankBranch(selectedBankData.branchNumber);
        setBankAccountNumber(selectedBankData.accountNumber);
        setCardForNewBank(selectedBankData.cardId);
        setForeignCurrencyCode(selectedBankData.foreignCurrencyCode);
        setIsEditingBank(true);

        setCreateBankModal(true); // Open the modal
    };
    const [bankFromBankPage, setBankFromBankPage] = useState(null);

    const fetchUserData = async () => {
        setLoading(true);
        try {
            const currentUser = await UserService.getCurrentUser();
            const currentToken = await UserService.getCurrentToken();
            if (currentUser && currentToken) {
                setUser(currentUser);
                setToken(currentToken);

                if (location.state && location.state.client) {

                    setClient(location.state.client);
                    setType(location.state.type)
                    await fetchEnums(currentUser, currentToken, location.state.client);
                }
                if (!isNaN(Number(location.state.bankFromBankPage))) {
                    setBankFromBankPage(location.state.bankFromBankPage);
                }
            } else {
                navigate('/login');
            }
        } catch (error) {
            console.error('Error fetching user data:', error);
        }
        setLoading(false);
    };

    useEffect(() => {
        if (tBanksData && !isNaN(Number(location.state.bankFromBankPage)) && processedCardsData) {
            selectedDenseTableRow(bankFromBankPage);
        }
    }, [tBanksData, bankFromBankPage, processedCardsData]);

    // Fetch enums and account cards
    const fetchEnums = async (user, token, client) => {
        setLoading(true);
        try {
            const tBanks = await EnumsService.GetAllTBanksByCaseId(
                client.caseId,
                token
            );
            const accountCardsResponse = await EnumsService.GetAccountCardsByCaseId(
                client.caseId,
                token
            );
            const accountCardsData = await accountCardsResponse.json();


            const tBanksRows = tBanks.map((bank) => [
                (() => {
                    const currencyObj = currencyOptions.find(o => o.value === bank.foreignCurrencyCode);
                    const code = currencyObj?.label;
                    const symbol = Constants.ForeignCurrencySymbol[code] || '';
                    return code ? `${code}${symbol ? ' (' + symbol + ')' : ''}` : '-';
                })(),
                bank.bankCode || "-",
                bank.bankName || "-",
                bank.branchNumber || "-",
                bank.accountNumber || "-",
                bank.cardId || "-",
                null, // Finsite status filled after eligibility loads
            ]);

            //processed account cards data
            if (accountCardsData && accountCardsData.length > 0) {
                const transformedData = accountCardsData.map((card) => ({
                    ...card,
                    value: card.cardId, // Set value to cardId
                    label: `${card.cardId} - ${card.name} `, // Set label to name
                }));
                setProcessedCardsData(transformedData);
            }
            const accountCardOptions = [{ name: t('AddAccountCard'), value1: 'AddItem' }, ...accountCardsData];
            setAccountCardOptionsForBank(accountCardOptions);
            setDenseTableRows(tBanksRows);
            setTBanksData(tBanks);
            setAccountCardsData(accountCardsData);



            // Fetching creditAccountOptions and debitAccountOptions
            const accountsResponse = await EnumsService.GetAccountCardsByCaseId(
                client.caseId,
                token
            );
            if (accountsResponse.ok) {
                const accountsData = await accountsResponse.json();

                // Map accountsData to extract name and cardId
                const accountOptions = accountsData.map((account) => ({
                    label: account.name, // Display name in the dropdown
                    value: account.cardId, // Use cardId as the value
                }));


                setAccountCardEditDownloadOptions(accountOptions);

                const accountCardOptionsEdit = [
                    { name: t("AddAccountCard"), value1: "AddItem" },
                    ...accountsData,
                ];

                setAccountCardOptions(accountCardOptionsEdit);
            }
            const palCodesResponse = await EnumsService.GetPalCodesByAccountantId(
                client.accountantId,
                token
            );
            // Process palCodes if response is okay
            if (palCodesResponse.ok) {
                const palCodesData = await palCodesResponse.json();
                const formattedPalCodes = palCodesData.map((palCode) => ({
                    label: palCode.palCodeId,
                    value: palCode.palCodeId,
                }));

                setFormattedPalCodeOptions(formattedPalCodes);
            }

            setIsClientDataFetched(true);
        } catch (error) {
            console.error(error);
            toast.error(t("fetchEnumsError"), error);
            setIsClientDataFetched(false);
        }
        setLoading(false);
    };

    // Fetch transactions data
    const fetchTransactionsData = async (endDate) => {
        const startDate = BALANCE_EPOCH;
        setLoading(true);
        try {
            const accountCardId = selectedAccountCard;
            if (!accountCardId) {
                setDenseTableCreditDebitRows([{}]);
                setFinsiteBalanceRows([]);
                setFinsiteDiffRows([]);
                setLoading(false);
                return;
            }
            const response = await ExternalTransactionsService.GetExternalTransactionsByDateRange(
                client.caseId,
                startDate,
                endDate,
                accountCardId,
                token
            );
            if (response.ok) {
                const data = await response.json();

                // data is of type ExternalTransactionSummaryDTO
                setTransactions(data.transactions || []);
                setSelectedTransactions([]);

                // Totals from the same date-filtered response (till selected month/year).
                // Flipped to match the transactions table: Credit (זכות) shows positive
                // totals, Debit (חובה) shows negative totals.
                const xtramBalance = data.balance ?? 0;
                const xtramCredit = data.debitAccountAmount ?? 0;
                const xtramDebit = data.creditAccountAmount ?? 0;
                setDenseTableCreditDebitRows([{
                    balance: beautifyNumber(xtramBalance),
                    credit: beautifyNumber(xtramCredit),
                    debit: beautifyNumber(xtramDebit),
                }]);
                // toast.success(t('Transactions fetched successfully'));

                // Side-by-side Finsite totals for linked accounts (same range).
                refreshFinsiteBalances(
                    accountCardId,
                    startDate,
                    endDate,
                    { balance: xtramBalance, credit: xtramCredit, debit: xtramDebit }
                );
            } else {
                const errorText = await response.text();
                toast.error(`${t('Error fetching transactions')}: ${errorText}`);
            }
        } catch (error) {
            toast.error(`${t('Error fetching transactions')}: ${error.message}`);
        }
        setLoading(false);
    };

    // Calculate totals for DenseTable
    const calculateDenseTableRows = (data) => {
        setDenseTableRows([
            {
                Credit: beautifyNumber(data.creditAccountAmount || 0),
                Debit: beautifyNumber(data.debitAccountAmount || 0),
                Balance: beautifyNumber(data.balance || 0),
            },
        ]);
    };



    // The period the rows list is limited to: in year mode, everything up to
    // the end of the selected year (not only that calendar year); in range
    // mode, the exact days the user picked.
    const getDisplayDateRange = (range = isRange, rangeStart = startDate, rangeEnd = endDate) => {
        if (!range) {
            const { end } = yearBounds(rangeEnd.getUTCFullYear());
            return { start: BALANCE_EPOCH, end };
        }
        return { start: startOfUtcDay(rangeStart), end: endOfUtcDay(rangeEnd) };
    };

    const applyPeriod = (start, end) => {
        localStorage.setItem('StartDate', start.toISOString());
        localStorage.setItem('EndDate', end.toISOString());
        setStartDate(start);
        setEndDate(end);
        fetchTransactionsData(end);
    };

    const handlePeriodYearChange = (year) => {
        if (!Number.isFinite(year)) return;
        const { start, end } = yearBounds(year);
        applyPeriod(start, end);
    };

    const handlePeriodStartChange = (newValue) => {
        const next = startOfUtcDay(new Date(newValue));
        if (isNaN(next.getTime())) return;
        // Keep the range valid rather than rejecting the pick.
        applyPeriod(next, next > endDate ? endOfUtcDay(next) : endDate);
    };

    const handlePeriodEndChange = (newValue) => {
        const next = endOfUtcDay(new Date(newValue));
        if (isNaN(next.getTime())) return;
        applyPeriod(next < startDate ? startOfUtcDay(next) : startDate, next);
    };

    const handlePeriodModeChange = (mode) => {
        const nextIsRange = mode === PERIOD_MODE.RANGE;
        if (nextIsRange === isRange) return;

        localStorage.setItem('IsRange', String(nextIsRange));
        setIsRange(nextIsRange);

        if (nextIsRange) {
            // Restore the range the user last worked with, if there was one.
            const { start, end } = lastRangeRef.current.start
                ? lastRangeRef.current
                : { start: startDate, end: endDate };
            applyPeriod(start, end);
            return;
        }

        lastRangeRef.current = { start: startDate, end: endDate };
        const { start, end } = yearBounds(endDate.getUTCFullYear());
        applyPeriod(start, end);
    };




    const displayDateRange = useMemo(
        () => getDisplayDateRange(),
        [isRange, startDate, endDate]
    );

    // The range bounds are UTC month boundaries, so they must be read as UTC to
    // avoid slipping a day in negative-offset timezones.
    const formatUtcDate = (date) =>
        `${String(date.getUTCDate()).padStart(2, '0')}/${String(date.getUTCMonth() + 1).padStart(2, '0')}/${date.getUTCFullYear()}`;

    // Handle search query change
    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };




    // Filtered and sorted transactions
    const filteredTransactions = useMemo(() => {
        let filtered = [...transactions];

        // The server returns the full history so the running balance column
        // stays correct; the selected period is applied to the list here.
        const { start: rangeStart, end: rangeEnd } = getDisplayDateRange();
        filtered = filtered.filter((transaction) => {
            if (!transaction.transactionDate) return true;
            const date = new Date(transaction.transactionDate);
            if (isNaN(date.getTime())) return true;
            return date >= rangeStart && date <= rangeEnd;
        });

        if (showOnlyWithDocuments) {
            filtered = filtered.filter((transaction) => !!transaction.documentUrl);
        }
        if (searchQuery) {
            filtered = filtered.filter((transaction) => {
                const query = searchQuery.toLowerCase();

                return (
                    (transaction.description && transaction.description.toLowerCase().includes(query)) ||
                    (transaction.reference1 && transaction.reference1.toLowerCase().includes(query)) ||
                    (transaction.reference2 && transaction.reference2.toLowerCase().includes(query)) ||
                    (transaction.debitAccount && transaction.debitAccount.toString().includes(query)) ||
                    (transaction.creditAccount && transaction.creditAccount.toString().includes(query))
                );
            });
        }

        if (sortConfig.key) {
            filtered.sort((a, b) => {
                const aValue = a[sortConfig.key] || '';
                const bValue = b[sortConfig.key] || '';

                if (sortConfig.type === 'number') {
                    return sortConfig.direction === 'ascending' ? aValue - bValue : bValue - aValue;
                } else if (sortConfig.type === 'date') {
                    return sortConfig.direction === 'ascending'
                        ? new Date(aValue) - new Date(bValue)
                        : new Date(bValue) - new Date(aValue);
                } else {
                    return sortConfig.direction === 'ascending'
                        ? aValue.localeCompare(bValue)
                        : bValue.localeCompare(aValue);
                }
            });
        }


        return filtered;
    }, [transactions, searchQuery, sortConfig, showOnlyWithDocuments, isRange, startDate, endDate]);

    const handleDownloadTransactionsExcel = () => {
        if (!filteredTransactions.length) return;

        const accountTitle = selectedTBank?.bankName && selectedTBank.bankName !== '-'
            ? selectedTBank.bankName
            : t('BankAccount');
        const cardLabel = selectedFormattedAccountCard?.label?.trim() || '';
        const periodLabel = `${formatUtcDate(displayDateRange.start)} — ${formatUtcDate(displayDateRange.end)}`;
        const clientLine = [client?.name, client?.caseId ? `(${client.caseId})` : '']
            .filter(Boolean)
            .join(' ');

        const header = [
            t('date'),
            t('Description'),
            t('Reference'),
            t('Debit'),
            t('Credit'),
            t('Balance'),
            t('MatchNumber'),
            t('Document'),
        ];

        let debitTotal = 0;
        let creditTotal = 0;
        const rows = filteredTransactions.map((tx) => {
            const debit = Number(tx.creditAccount) || 0;
            const credit = Number(tx.debitAccount) || 0;
            debitTotal += debit;
            creditTotal += credit;
            return [
                tx.transactionDate ? formatDate(tx.transactionDate) : '',
                tx.description || '',
                tx.reference1 || '',
                debit,
                credit,
                Number(tx.balance) || 0,
                tx.matchNumber == null || tx.matchNumber === -1 ? '' : tx.matchNumber,
                tx.documentUrl ? t('Yes') : '',
            ];
        });

        rows.push([
            t('Total'),
            '',
            `${t('Rows')}: ${filteredTransactions.length}`,
            debitTotal,
            creditTotal,
            '',
            '',
            '',
        ]);

        const sheetName = String(t('BankTransactions') || 'Transactions')
            .replace(/[\\/?*[\]:]/g, ' ')
            .trim()
            .slice(0, 31) || 'Transactions';

        const workbook = buildReportWorkbook({
            sheetName,
            titleLines: [
                clientLine || accountTitle,
                t('BankTransactions'),
                [accountTitle, cardLabel, `${t('Period')}: ${periodLabel}`].filter(Boolean).join(' | '),
            ],
            header,
            rows,
            colWidths: [14, 40, 18, 16, 16, 16, 14, 12],
            numericColIndexes: [3, 4, 5],
            totalRowIndexes: [rows.length - 1],
        });
        if (direction === 'ltr') applyWorkbookLtr(workbook);

        const safePart = (value) => String(value || '')
            .replace(/[\\/:*?"<>|]/g, '')
            .replace(/\s+/g, ' ')
            .trim();
        const fileName = [
            safePart(t('BankTransactions')) || 'BankTransactions',
            safePart(accountTitle),
            formatUtcDate(displayDateRange.start).split('/').join('-'),
            formatUtcDate(displayDateRange.end).split('/').join('-'),
        ].filter(Boolean).join('_') + '.xlsx';

        XLSX.writeFile(workbook, fileName);
    };

    // Map bank rows into the shape DuplicateEntriesModal expects. Duplicate key mirrors the
    // Excel / Finsite importers: date + reference + signed amount.
    const bankDuplicateRows = useMemo(() => {
        return (filteredTransactions || []).map((tx) => {
            const signedAmount =
                tx.foreignCurrencyAmount != null && tx.foreignCurrencyAmount !== 0
                    ? Number(tx.foreignCurrencyAmount)
                    : (Number(tx.debitAccount) || 0) - (Number(tx.creditAccount) || 0);
            return {
                id: tx.id,
                documentDate: tx.transactionDate,
                reference: tx.reference1 || '',
                amount: signedAmount,
                description: tx.description || '',
                matchNumber: tx.matchNumber,
                uploadNumber: tx.uploadNumber,
            };
        });
    }, [filteredTransactions]);

    // Handle sort change
    const handleSortChange = (key, type) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction, type });
    };

    // Handle edit transaction
    const handleEdit = (transaction, index) => {
        // Matched transactions open the dialog in read-only "view" mode (to inspect the document
        // in a large preview); only unmatched transactions can actually be edited.
        const isMatched =
            transaction.matchNumber != null &&
            transaction.matchNumber !== -1 &&
            transaction.matchNumber !== 0;
        setEditViewOnly(isMatched);
        setOldEditedTransaction(transaction);
        if (transaction.creditAccount > 0) {
            setEditedTransaction({
                ...transaction,
                amount: -transaction.creditAccount
            });
        } else {
            setEditedTransaction({
                ...transaction,
                amount: transaction.debitAccount
            });
        }
        setEditBankTransactionDialog(true);
    };

    const handleViewTransactionMatch = async (transaction) => {
        const matchNumber = transaction?.matchNumber ?? transaction?.MatchNumber;
        if (matchNumber == null || matchNumber === -1 || matchNumber === 0) {
            return;
        }
        if (!client?.caseId || !selectedAccountCard) {
            toast.error(t('MatchDetailsNotFound'));
            return;
        }

        try {
            setIsLoadingTransactionMatch(true);
            setIsTransactionMatchModalOpen(true);
            setSelectedTransactionMatch(null);

            const response = await ExternalTransactionsService.GetByMatch(
                client.caseId,
                selectedAccountCard,
                matchNumber,
                token
            );

            if (response.ok) {
                const match = await response.json();
                setSelectedTransactionMatch(match);
            } else if (response.status === 404) {
                toast.error(t('MatchDetailsNotFound'));
                setIsTransactionMatchModalOpen(false);
            } else {
                const errorText = await response.text();
                toast.error(errorText || t('MatchDetailsNotFound'));
                setIsTransactionMatchModalOpen(false);
            }
        } catch (error) {
            console.error('Failed to fetch transaction match', error);
            toast.error(t('MatchDetailsNotFound'));
            setIsTransactionMatchModalOpen(false);
        } finally {
            setIsLoadingTransactionMatch(false);
        }
    };

    const handleCloseTransactionMatchModal = () => {
        setIsTransactionMatchModalOpen(false);
        setSelectedTransactionMatch(null);
    };

    const handleRemoveMatchClick = (match) => {
        const card = accountCardsData.find(
            (item) => String(item.cardId) === String(selectedAccountCard)
        );
        setSelectedMatchForRemoval({
            ...match,
            accountCardName: card?.name || selectedFormattedAccountCard?.name || '',
        });
        setOpenRemoveMatchDialog(true);
    };

    const handleCloseRemoveMatchDialog = () => {
        setOpenRemoveMatchDialog(false);
        setSelectedMatchForRemoval(null);
    };

    const handleConfirmRemoveMatch = async () => {
        if (!selectedMatchForRemoval?.id || !selectedMatchForRemoval?.caseId) {
            return;
        }
        try {
            setIsRemovingMatch(true);
            const response = await ExternalTransactionsService.DeleteMatch(
                {
                    CaseId: selectedMatchForRemoval.caseId,
                    Id: selectedMatchForRemoval.id,
                },
                token
            );
            if (response.ok) {
                toast.success(t('MatchDeletedSuccessfully'));
                handleCloseRemoveMatchDialog();
                handleCloseTransactionMatchModal();
                await fetchTransactionsData(endDate);
            } else {
                toast.error(t('ErrorDeletingMatch'));
            }
        } catch (error) {
            console.error('Error deleting match:', error);
            toast.error(t('ErrorDeletingMatch'));
        } finally {
            setIsRemovingMatch(false);
        }
    };

    // Handle add new transaction
    const [openAddTransactionDialog, setOpenAddTransactionDialog] = useState(false);
    const handleAddClick = () => {
        setOpenAddTransactionDialog(true);
    };

    const handleCloseAddTransactionDialog = () => {
        setOpenAddTransactionDialog(false);
    };
    const handleChangeBankForCreate = (value) => {
        setSelectedBankForCreate(value);
    };

    const handleChangeCreateBank = (field, value) => {
        if (field === "bankBranch") {
            setBankBranch(value);
        } else if (field === "bankAccountNumber") {
            setBankAccountNumber(value);
        } else if (field === "itemName") {
            // setItemName(value);
        }
        else if (field === "foreignCurrencyCode") {
            setForeignCurrencyCode(value);
        }

    };
    const handleChangeSetCardForNewBank = (value) => {


        if (value) {
            setCardForNewBank(value);
        } else {
            setCardForNewBank(null);
        }
    }

    const handleEditBank = async () => {
        try {
            const bankName =
                Constants.BankNames[parseInt(selectedBankForCreate.value)];
            const bankCode = selectedBankForCreate.value;

            if (!selectedBankForCreate || !bankBranch || !bankAccountNumber) {
                toast.error(t("PleaseFillAllFields"));
                return;
            }

            if (!oldBank) {
                toast.error(t("OldBankDataNotFound"));
                return;
            }

            const response = await EnumsService.EditTBank(
                client.caseId, // Use the CaseId from the edited bank
                oldBank.bankCode, // Old bank code
                oldBank.accountNumber, // Old account number
                bankName, // New bank name
                bankCode, // New bank code
                bankBranch, // New branch number
                bankAccountNumber, // New account number
                foreignCurrencyCode,
                cardForNewBank.toString(),
                token // Authorization token
            );

            if (response.ok) {
                toast.success(t("BankAccountUpdatedSuccessfully"));
                fetchEnums(user, token, client); // Fetch updated data
                handleCloseCreateModal(); // Close the modal
            } else {
                toast.error(t("FailedToUpdateBankAccount"));
            }
        } catch (error) {
            console.error(error);
            toast.error(t("ErrorUpdatingBankAccount"));
        }
    };


    const handleAddBank = async () => {
        try {
            const bankName =
                Constants.BankNames[parseInt(selectedBankForCreate.value)];
            const bankCode = selectedBankForCreate.value;

            if (!selectedBankForCreate || !bankBranch || !bankAccountNumber) {
                toast.error(t("PleaseFillAllFields"));
                return;
            }

            const response = await EnumsService.AddTBank(
                client.caseId,
                bankName,
                bankCode,
                bankBranch,
                bankAccountNumber,
                foreignCurrencyCode,
                cardForNewBank.toString(),
                token
            );
            if (response.ok) {
                toast.success(t("BankAccountAddedSuccessfully"));
                fetchEnums(user, token, client);
                handleCloseCreateModal();
            } else {
                toast.error(t("FailedToAddBankAccount"));
            }
        } catch (error) {
            console.error(error);
            toast.error(t("ErrorAddingBankAccount"));
        }
    };

    const handleAddOrEditBank = () => {

        if (isEditingBank && editedBank) {
            handleEditBank();
        } else {
            handleAddBank();
        }
        handleCloseCreateModal(); // Close the modal after adding/editing
    };

    const handleRemoveBank = async () => {
        const result = await Swal.fire({
            title: t('AreYouSure'),
            text: t('DeleteBankConfirmation'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('YesDeleteIt'),
            cancelButtonText: t('No'),
            customClass: {
                container: 'swal-high-zindex'
            }
        });

        if (result.isConfirmed) {
            try {
                const response = await EnumsService.DeleteTBank(
                    client.caseId,
                    bankAccountNumber,
                    selectedBankForCreate.value,
                    token
                );
                if (response.ok) {
                    toast.success(t('BankDeletedSuccessfully'));
                    fetchEnums(user, token, client);
                    handleCloseCreateModal();
                } else {
                    toast.error(t("BankFailedToDelete"));
                }
            } catch (error) {
                console.error("BankFailedToDelete");
                toast.error(t("BankFailedToDelete"));
            }
        }
    };

    const handleDeleteSelectedBanks = async (selectedIndices) => {
        if (!selectedIndices || selectedIndices.length === 0) {
            return;
        }

        const banksToDelete = selectedIndices
            .map((index) => tBanksData[index])
            .filter(Boolean);

        if (banksToDelete.length === 0) {
            return;
        }

        const result = await Swal.fire({
            title: t('AreYouSure'),
            text: `${t('DeleteBankConfirmation')} (${banksToDelete.length})`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('YesDeleteIt'),
            cancelButtonText: t('No'),
            customClass: {
                container: 'swal-high-zindex'
            }
        });

        if (!result.isConfirmed) {
            return;
        }

        setLoading(true);
        try {
            const responses = await Promise.all(
                banksToDelete.map((bank) =>
                    EnumsService.DeleteTBank(
                        client.caseId,
                        bank.accountNumber,
                        bank.bankCode,
                        token
                    )
                )
            );

            const failedCount = responses.filter((response) => !response.ok).length;

            if (failedCount === 0) {
                toast.success(t('BankDeletedSuccessfully'));
            } else if (failedCount === banksToDelete.length) {
                toast.error(t('BankFailedToDelete'));
            } else {
                toast.warn(`${t('BankFailedToDelete')} (${failedCount}/${banksToDelete.length})`);
            }

            await fetchEnums(user, token, client);
        } catch (error) {
            console.error('Error deleting banks:', error);
            toast.error(t('BankFailedToDelete'));
        }
        setLoading(false);
    };

    // Handle import click
    const handleImportClick = () => {
        if (!selectedTBank) {
            toast.info(t("YouNeedToSelectABankFirst"));
            return;
        }

        // Search for a matching bank entry in tBanks
        const bankEntry = tBanksData.find(
            (bank) => bank.cardId?.toString() === mainCard.cardId?.toString()
        );

        // If a matching bank entry is found and it has a corresponding URL
        if (bankEntry && Constants.BankUrls[parseInt(bankEntry.bankCode)]) {
            // Open the bank's URL in a new tab
            window.open(Constants.BankUrls[parseInt(bankEntry.bankCode)], "_blank");
        }

        // Open the dialog
        setIsSumitImport(false);
        setOpenImportDialog(true);
    };

    // Handle Sumit import click
    const handleSumitImportClick = () => {
        if (!selectedTBank) {
            toast.info(t("YouNeedToSelectABankFirst"));
            return;
        }

        setIsSumitImport(true);
        setIsHashavshevetImport(false);
        setOpenSumitImportDialog(true);
    };

    // Handle Hashavshevet import click
    const handleHashavshevetImportClick = () => {
        if (!selectedTBank) {
            toast.info(t("YouNeedToSelectABankFirst"));
            return;
        }

        setIsHashavshevetImport(true);
        setIsSumitImport(false);
        setOpenHashavshevetImportDialog(true);
    };

    // Preview Finsite transactions for the selected account/date range, then open the
    // same ImportPreviewModal used by Excel/Sumit import. Confirm later runs the real sync.
    const handleFinsiteFetch = async () => {
        if (!selectedTBank || !selectedAccountCard) {
            toast.info(t("YouNeedToSelectABankFirst"));
            return;
        }

        const toIsoDate = (d) => {
            const date = new Date(d);
            return isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
        };

        setLoading(true);
        try {
            const response = await FinsiteService.PreviewCaseAccount(
                client.caseId,
                selectedAccountCard,
                toIsoDate(finsiteFrom),
                toIsoDate(finsiteTo),
                token
            );

            if (response.ok) {
                const result = await response.json();
                if (result.success || result.status === 'PartialSuccess') {
                    const newCount = result.transactionsCount || 0;
                    const dupCount = result.duplicateCount || (result.duplicateTransactions || []).length || 0;
                    const docsAvailable = result.documentsAvailableToAttach
                        || (result.duplicateTransactions || []).filter((r) => r.canAttachDocument).length
                        || 0;
                    const docsRefreshable = result.documentsAvailableToRefresh
                        || (result.duplicateTransactions || []).filter((r) => r.canRefreshDocument || r.hasChequeImage).length
                        || 0;
                    if (newCount === 0 && dupCount === 0) {
                        toast.info(t("FinsiteFetchNoNew"));
                        setFinsiteDialogOpen(false);
                    } else {
                        setPreviewData({
                            transactions: result.transactions || [],
                            transactionsCount: newCount,
                            duplicateTransactions: result.duplicateTransactions || [],
                            duplicateCount: dupCount,
                            documentsAvailableToAttach: docsAvailable,
                            documentsAvailableToRefresh: docsRefreshable,
                            balanceBefore: result.balanceBefore ?? 0,
                            balanceAfter: result.balanceAfter ?? 0,
                            bankBalance: result.bankBalance ?? null,
                            systemBalance: result.systemBalance ?? result.balanceBefore ?? 0,
                            totalDebit: result.totalDebit || 0,
                            totalCredit: result.totalCredit || 0,
                            skippedCount: result.skippedCount || dupCount,
                            isFinsiteImport: true,
                            finsiteFrom: toIsoDate(finsiteFrom),
                            finsiteTo: toIsoDate(finsiteTo),
                        });
                        setPreviewFile(null);
                        setFinsiteDialogOpen(false);
                        setOpenPreviewModal(true);
                    }
                } else if (result.status === "NoMapping") {
                    toast.error(t("FinsiteNoMapping"));
                } else if (result.status === "NoActiveCredential") {
                    toast.error(t("FinsiteNoCredential"));
                } else if (result.status === "AuthFailed") {
                    toast.error(t("FinsiteAuthFailed"));
                } else {
                    toast.error(t("FinsiteFetchFailed"));
                }
            } else {
                toast.error(t("FinsiteFetchFailed"));
            }
        } catch (error) {
            console.error("Finsite preview failed:", error);
            toast.error(t("FinsiteFetchFailed"));
        }
        setLoading(false);
    };

    const handleConfirmFinsiteImport = async (options = {}) => {
        if (!previewData?.isFinsiteImport || !selectedAccountCard) return;

        const attachDocumentsForExisting = options.attachDocumentsForExisting !== false;
        const forceRefreshDocuments = !!options.forceRefreshDocuments;

        setLoading(true);
        try {
            const response = await FinsiteService.SyncCaseAccount(
                client.caseId,
                selectedAccountCard,
                previewData.finsiteFrom,
                previewData.finsiteTo,
                token,
                attachDocumentsForExisting,
                forceRefreshDocuments
            );

            if (response.ok) {
                const result = await response.json();
                if (result.success) {
                    const imported = result.importedCount || 0;
                    const docsAttached = result.documentsAttachedCount || 0;
                    if (imported > 0 && docsAttached > 0) {
                        toast.success(
                            `${t("FinsiteFetchSuccess")} (${imported}) · ${t("FinsiteDocumentsSaved")} (${docsAttached})`
                        );
                    } else if (imported > 0) {
                        toast.success(
                            `${t("FinsiteFetchSuccess")} (${imported})`
                        );
                    } else if (docsAttached > 0) {
                        toast.success(
                            `${t("FinsiteDocumentsSaved")} (${docsAttached})`
                        );
                    } else {
                        toast.info(t("FinsiteFetchNoNew"));
                    }
                    setOpenPreviewModal(false);
                    setPreviewData(null);
                    await fetchTransactionsData(endDate);
                } else if (result.status === "NoMapping") {
                    toast.error(t("FinsiteNoMapping"));
                } else if (result.status === "NoActiveCredential") {
                    toast.error(t("FinsiteNoCredential"));
                } else if (result.status === "AuthFailed") {
                    toast.error(t("FinsiteAuthFailed"));
                } else {
                    toast.error(t("FinsiteFetchFailed"));
                }
            } else {
                toast.error(t("FinsiteFetchFailed"));
            }
        } catch (error) {
            console.error("Finsite import failed:", error);
            toast.error(t("FinsiteFetchFailed"));
        }
        setLoading(false);
    };

    const loadFinsiteDiscovery = async () => {
        if (!client?.accountantId || !token) return;
        setFinsiteDiscovering(true);
        try {
            const [cRes, aRes, bRes] = await Promise.all([
                FinsiteService.GetCompanies(client.accountantId, token),
                FinsiteService.GetAccounts(client.accountantId, token),
                FinsiteService.GetBookingAccounts(client.accountantId, token),
            ]);
            const companies = cRes.ok ? await cRes.json() : [];
            const accounts = aRes.ok ? await aRes.json() : [];
            const bookingAccounts = bRes.ok ? await bRes.json() : [];
            setFinsiteCompanies(Array.isArray(companies) ? companies : []);
            setFinsiteAccounts(Array.isArray(accounts) ? accounts : []);
            setFinsiteBookingAccounts(Array.isArray(bookingAccounts) ? bookingAccounts : []);

            const matchedCompany = (Array.isArray(companies) ? companies : []).find(
                (co) => String(co.suggestedCaseId) === String(client.caseId)
            );
            setFinsiteLinkForm({
                companyEntryId: matchedCompany?.entryId ?? '',
                accountId: '',
                bookingAccountId: '',
            });
        } catch (error) {
            console.error('Finsite discovery failed:', error);
            toast.error(t('FinsiteDiscoveryFailed'));
        } finally {
            setFinsiteDiscovering(false);
        }
    };

    const handleOpenFinsiteLinkDialog = async () => {
        if (!selectedTBank || !selectedAccountCard) {
            toast.info(t('YouNeedToSelectABankFirst'));
            return;
        }
        setFinsiteLinkDialogOpen(true);
        await loadFinsiteDiscovery();
    };

    const handleFinsiteAutoLink = async () => {
        if (!client?.accountantId || !client?.caseId) return;

        const confirmStart = await Swal.fire({
            title: t('FinsiteAutoLink'),
            text: t('FinsiteAutoLinkConfirm'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#1B8A5A',
            cancelButtonColor: '#6c757d',
            confirmButtonText: t('Preview'),
            cancelButtonText: t('Cancel'),
            customClass: { container: 'swal-high-zindex' },
        });
        if (!confirmStart.isConfirmed) return;

        setFinsiteAutoLinking(true);
        try {
            const suggestionsRes = await FinsiteService.GetAutoLinkClientSuggestions(
                client.accountantId,
                client.caseId,
                token
            );
            if (!suggestionsRes.ok) {
                if (suggestionsRes.status === 401 || suggestionsRes.status === 403) {
                    toast.error(t('FinsiteAuthFailed'));
                } else {
                    toast.error(t('FinsiteAutoLinkFailed'));
                }
                return;
            }

            const preview = await suggestionsRes.json();
            const suggestions = Array.isArray(preview?.suggestions) ? preview.suggestions : [];
            const matched = suggestions.filter((s) => s.status === 'Matched');
            const alreadyMapped = suggestions.filter((s) => s.status === 'AlreadyMapped');
            const unmatched = suggestions.filter(
                (s) => s.status !== 'Matched' && s.status !== 'AlreadyMapped'
            );

            if (matched.length === 0) {
                await Swal.fire({
                    title: t('FinsiteAutoLink'),
                    text: t('FinsiteAutoLinkNoMatch'),
                    icon: 'info',
                    confirmButtonColor: '#304FFF',
                    confirmButtonText: t('OK'),
                    customClass: { container: 'swal-high-zindex' },
                    html: alreadyMapped.length || unmatched.length
                        ? `<div style="text-align:start;margin-top:8px;font-size:13px;color:#666">
                            ${alreadyMapped.length ? `<div>${t('FinsiteAlreadyMapped')}: <b>${alreadyMapped.length}</b></div>` : ''}
                            ${unmatched.length ? `<div>${t('FinsiteUnmatched')}: <b>${unmatched.length}</b></div>` : ''}
                           </div>`
                        : undefined,
                });
                await refreshFinsiteEligibility();
                return;
            }

            const rowsHtml = matched
                .map((s) => {
                    const booking = formatFinsiteBookingLabel({
                        subtype: s.bookingAccountSubtype,
                        currency: s.currency,
                        cc4Digits: getCc4Digits(s),
                    });
                    const bank = [s.bankCode, s.branch, s.accountNumber].filter(Boolean).join('/') || '—';
                    const card = s.cardLabel || s.cardId || '—';
                    return `<tr>
                        <td style="padding:6px;border-bottom:1px solid #eee;text-align:start">${s.finsiteCompanyName || s.taxRegistrationNumber || '—'}</td>
                        <td style="padding:6px;border-bottom:1px solid #eee;text-align:start">${booking || '—'}</td>
                        <td style="padding:6px;border-bottom:1px solid #eee;text-align:start">${bank}</td>
                        <td style="padding:6px;border-bottom:1px solid #eee;text-align:start">${card}</td>
                    </tr>`;
                })
                .join('');

            const previewResult = await Swal.fire({
                title: t('FinsiteAutoLinkPreviewTitle'),
                icon: 'info',
                width: 720,
                html: `
                    <div style="text-align:start;margin-bottom:10px;font-size:14px">
                        ${t('FinsiteAutoLinkPreviewSummary', {
                            matched: matched.length,
                            alreadyMapped: alreadyMapped.length,
                            unmatched: unmatched.length,
                        })}
                    </div>
                    <div style="max-height:280px;overflow:auto;border:1px solid #e5e7eb;border-radius:8px">
                        <table style="width:100%;border-collapse:collapse;font-size:13px">
                            <thead>
                                <tr style="background:#F5F7FF">
                                    <th style="padding:8px;text-align:start">${t('FinsiteCompany')}</th>
                                    <th style="padding:8px;text-align:start">${t('FinsiteBookingAccount')}</th>
                                    <th style="padding:8px;text-align:start">${t('FinsiteAccount')}</th>
                                    <th style="padding:8px;text-align:start">${t('AccountCardNumber')}</th>
                                </tr>
                            </thead>
                            <tbody>${rowsHtml}</tbody>
                        </table>
                    </div>
                `,
                showCancelButton: true,
                confirmButtonColor: '#1B8A5A',
                cancelButtonColor: '#6c757d',
                confirmButtonText: t('FinsiteAutoLinkConfirmCommit'),
                cancelButtonText: t('Cancel'),
                customClass: { container: 'swal-high-zindex' },
            });

            if (!previewResult.isConfirmed) return;

            const response = await FinsiteService.AutoLinkClient(
                client.accountantId,
                client.caseId,
                token
            );
            if (response.ok) {
                const data = await response.json();
                const created = data?.created ?? 0;
                if (created > 0) {
                    toast.success(
                        t('FinsiteAutoLinkSuccess', { created, unmatched: data?.unmatched ?? 0 })
                    );
                } else {
                    toast.info(t('FinsiteAutoLinkNoMatch'));
                }
                await refreshFinsiteEligibility();
            } else if (response.status === 401 || response.status === 403) {
                toast.error(t('FinsiteAuthFailed'));
            } else {
                toast.error(t('FinsiteAutoLinkFailed'));
            }
        } catch (error) {
            console.error('Finsite auto-link failed:', error);
            toast.error(t('FinsiteAutoLinkFailed'));
        } finally {
            setFinsiteAutoLinking(false);
        }
    };

    const handleFinsiteCreateMapping = async () => {
        if (
            !client?.accountantId
            || !client?.caseId
            || !selectedAccountCard
            || !finsiteLinkForm.companyEntryId
            || !finsiteLinkForm.bookingAccountId
        ) {
            toast.error(t('FinsiteMappingFieldsRequired'));
            return;
        }

        const comp = finsiteCompanies.find(
            (c) => String(c.entryId) === String(finsiteLinkForm.companyEntryId)
        );
        const ba = finsiteBookingAccounts.find(
            (b) => String(b.entryId) === String(finsiteLinkForm.bookingAccountId)
        );

        setFinsiteSavingMapping(true);
        try {
            const response = await FinsiteService.CreateMapping(
                {
                    accountantId: client.accountantId,
                    finsiteCompanyEntryId: Number(finsiteLinkForm.companyEntryId),
                    finsiteCompanyName: comp?.name,
                    taxRegistrationNumber: comp?.taxRegistrationNumber,
                    caseId: client.caseId,
                    finsiteAccountId: Number(finsiteLinkForm.accountId || ba?.accountId || 0),
                    finsiteBookingAccountId: Number(finsiteLinkForm.bookingAccountId),
                    bookingAccountSubtype: ba?.subtype,
                    currency: ba?.currency,
                    cc4Digits: getCc4Digits(ba),
                    cardId: selectedAccountCard,
                    isActive: true,
                },
                token
            );
            if (response.ok) {
                toast.success(t('FinsiteMappingSaved'));
                setFinsiteLinkDialogOpen(false);
                await refreshFinsiteEligibility();
            } else if (response.status === 409) {
                toast.error(t('FinsiteMappingExists'));
            } else {
                toast.error(t('FinsiteMappingSaveFailed'));
            }
        } catch (error) {
            console.error('Finsite mapping save failed:', error);
            toast.error(t('FinsiteMappingSaveFailed'));
        } finally {
            setFinsiteSavingMapping(false);
        }
    };

    const filteredFinsiteAccounts = finsiteAccounts.filter(
        (a) => !finsiteLinkForm.companyEntryId
            || String(a.companyId) === String(finsiteLinkForm.companyEntryId)
    );
    const filteredFinsiteBooking = finsiteBookingAccounts.filter(
        (b) => !finsiteLinkForm.accountId
            || String(b.accountId) === String(finsiteLinkForm.accountId)
    );

    const formatFinsiteBookingLabel = (booking) => {
        if (!booking) return '';
        const rawSubtype = String(booking.subtype || '').replace(/[\s_-]+/g, '');
        const subtypeKey = `FinsiteSubtype${rawSubtype}`;
        const subtypeLabel = t(subtypeKey);
        const subtype =
            subtypeLabel !== subtypeKey && rawSubtype
                ? subtypeLabel
                : (booking.subtype || '');
        const currencyKey = booking.currency
            ? `FinsiteCurrency${String(booking.currency).toUpperCase()}`
            : '';
        const currencyLabel = currencyKey ? t(currencyKey) : '';
        const currency =
            currencyKey && currencyLabel !== currencyKey
                ? currencyLabel
                : (booking.currency || '');
        const cc4Digits = getCc4Digits(booking);
        const cc = cc4Digits ? ` ••${cc4Digits}` : '';
        return `${subtype} ${currency}${cc}`.trim();
    };

    const handleOpenBankAddTransactionDialog = () => {
        if (!selectedTBank) {
            toast.info(t("YouNeedToSelectABankFirst"));
            return;
        }
        setAddBankTransactionDialog(true);
    };

    const handleNavigateToBankMatch = () => {
        const previosPage = '/BankPages';
        viewBankTransactions('BankMatching', selectedTBankIndex, previosPage);
    };

    const handleCloseBankAddTransactionDialog = () => {
        setAddBankTransactionDialog(false);
        setNewBankEditableItem({
            caseId: '',
            accountCodeId: '',
            accountCodeName: '',
            description: '',
            reference: '',
            amount: '',
            transactionDate: ''
        });

    };

    const handleCloseBankEditTransactionDialog = () => {

        setEditBankTransactionDialog(false);
        setEditViewOnly(false);
        setEditedTransaction({
            caseId: '',
            accountCodeId: '',
            accountCodeName: '',
            description: '',
            creditAccount: 0,
            debitAccount: 0,
            reference: '',
            amount: '',
            transactionDate: ''
        });
    };




    const handleAddBankTransaction = async () => {
        try {


            const isForeign = selectedTBank?.foreignCurrencyCode != null;
            const amountValue = parseFloat(newBankEditableItem.amount || 0);

            const updatedBankItem = {
                ...newBankEditableItem,
                caseId: client.caseId,
                accountCodeId: mainCard.cardId,
                accountCodeName: mainCard.name,

                // ─── domestic vs. foreign ───
                amount: isForeign ? 0 : amountValue,
                foreignCurrencyAmount: isForeign ? amountValue : null,
                foreignCurrencyCode: isForeign ? selectedTBank.foreignCurrencyCode : null,
            };



            const response = await ExternalTransactionsService.CreateTransaction(updatedBankItem, token);

            if (response.ok) {

                setNewBankEditableItem({
                    caseId: '',
                    accountCodeId: '',
                    accountCodeName: '',
                    description: '',
                    reference: '',
                    amount: '',
                    transactionDate: new Date()
                });
                handleCloseBankAddTransactionDialog();
                await fetchTransactionsData(endDate);
            } else {
                const errorMessage = await response.text();

                if (errorMessage === "A transaction with the same reference already exists.") {
                    toast.error(t('TransactionWithSameReferenceExists'));
                }
                toast.error(t('TransactionWithSameReferenceExists'));
            }
        } catch (error) {

            toast.error(t('FailedToCreateBankCommand'));
        }
    };

    const handleDelete = async () => {
        try {
            const isFinsiteRow =
                editedTransaction?.finsiteEntryId != null
                || editedTransaction?.FinsiteEntryId != null;

            let blockFinsiteReimport = false;
            if (isFinsiteRow) {
                const confirmResult = await Swal.fire({
                    title: t('confirmDeleteTitle'),
                    text: t('DeleteFinsiteBlockPrompt'),
                    icon: 'warning',
                    showDenyButton: true,
                    showCancelButton: true,
                    confirmButtonColor: '#d32f2f',
                    denyButtonColor: '#E57C22',
                    cancelButtonColor: '#6c757d',
                    confirmButtonText: t('DeleteAndBlockFinsite'),
                    denyButtonText: t('DeleteOnly'),
                    cancelButtonText: t('Cancel'),
                });
                if (confirmResult.isDismissed) return;
                blockFinsiteReimport = confirmResult.isConfirmed;
            }

            const deleteDTO = {
                Id: editedTransaction.id,
                CaseId: client.caseId,
                AccountCodeId: mainCard.cardId,
                Credit: editedTransaction.creditAccount,
                Debit: editedTransaction.debitAccount,
                BlockFinsiteReimport: blockFinsiteReimport,
            };

            const response = await ExternalTransactionsService.DeleteExternalTransaction(deleteDTO, token);

            if (response.ok) {
                setEditedTransaction({
                    caseId: '',
                    accountCodeId: '',
                    accountCodeName: '',
                    description: '',
                    creditAccount: 0,
                    debitAccount: 0,
                    reference: '',
                    amount: '',
                    transactionDate: ''
                });
                handleCloseBankEditTransactionDialog();
                await fetchTransactionsData(endDate);
            } else {
                const errorMessage = await response.text();
                toast.error(t('FailedToDeleteTransaction'));
            }
        } catch (error) {
            toast.error('FailedToDeleteTransaction');
        }
    };


    const handleEditBankTransaction = async () => {
        try {
            /* ───── helpers & flags ───── */
            const isForeign = selectedTBank?.foreignCurrencyCode != null;
            const amtRaw = parseFloat(editedTransaction.amount ?? 0);  // may be ±
            const isCredit = amtRaw < 0;                                 // sign
            const absAmt = Math.abs(amtRaw);

            /* ───── base object ───── */
            let updatedBankItem = {
                ...editedTransaction,
                caseId: client.caseId,
                accountCodeId: mainCard.cardId,
                accountCodeName: mainCard.name,
                reference: editedTransaction.reference1,

                // domestic vs. foreign
                amount: isForeign ? 0 : amtRaw,
                foreignCurrencyAmount: isForeign ? amtRaw : null,
                foreignCurrencyCode: isForeign ? selectedTBank.foreignCurrencyCode : null,
            };

            /* ───── debit / credit columns (for UI consistency) ───── */
            if (isCredit) {
                // want a credit row
                updatedBankItem = {
                    ...updatedBankItem,
                    debitAccount: 0,
                    creditAccount: absAmt
                };
            } else {
                // want a debit row
                updatedBankItem = {
                    ...updatedBankItem,
                    debitAccount: absAmt,
                    creditAccount: 0
                };
            }

            /* ───── call backend ───── */
            const response = await ExternalTransactionsService.UpdateExternalTransaction(
                updatedBankItem,
                oldEditedTransaction.creditAccount,
                oldEditedTransaction.debitAccount,
                token
            );

            if (response.ok) {
                setEditedTransaction({
                    caseId: '',
                    accountCodeId: '',
                    accountCodeName: '',
                    description: '',
                    creditAccount: 0,
                    debitAccount: 0,
                    reference: '',
                    amount: '',
                    transactionDate: ''
                });
                handleCloseBankEditTransactionDialog();
                await fetchTransactionsData(endDate);
            } else {
                toast.error(t('FailedToUpdateBankCommand'));
            }
        } catch (error) {
            toast.error(t('FailedToUpdateBankCommand'));
        }
    };

    const changeNewBankTransaction = (field, value) => {
        setNewBankEditableItem(prevState => ({
            ...prevState,
            [field]: value
        }));

    };

    const changeEditedBankTransaction = (field, value) => {
        setEditedTransaction(prevState => ({
            ...prevState,
            [field]: value
        }));
    };

    const [newBankEditableItem, setNewBankEditableItem] = useState({
        caseId: '',
        accountCodeId: '',
        accountCodeName: '',
        description: '',
        reference: '',
        amount: '',
        foreignCurrencyAmount: '',
        foreignCurrencyCode: null,
        transactionDate: new Date()
    });

    // Format date
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = ('0' + date.getDate()).slice(-2);
        const month = ('0' + (date.getMonth() + 1)).slice(-2);
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const formatAccountLabel = (accountId) => {
        if (!accountId) return '';
        const card = accountCardsData.find(
            (item) => String(item.cardId) === String(accountId)
        );
        return card ? `${card.cardId} - ${card.name}` : String(accountId);
    };

    // Handle update transaction

    // Handle cancel edit
    const handleCancelEdit = () => {
        setEditedTransaction(null);
    };

    // Handle isMonthly change effect
    useEffect(() => {
        if (client) {

            fetchTransactionsData(endDate);

        }
    }, [isMonthly, client]);

    // Handle account card change effect
    useEffect(() => {
        setSelectedTransactions([]);
        if (selectedAccountCard && client) {
            fetchTransactionsData(endDate);
        }
    }, [selectedAccountCard]);

    // Handle create bank modal
    const handleOpenCreateModal = () => {
        setIsEditingBank(false);
        setCreateBankModal(true);
    };
    const handleCloseCreateModal = () => {
        setCreateBankModal(false);
        setSelectedBankForCreate(null);
        setBankBranch("");
        setBankAccountNumber("");
        setForeignCurrencyCode(null);
        setCardForNewBank(null)
        setIsEditingBank(false);
    };
    const handlePreview = async (file, useXtramTemplate = false, useIsracardTemplate = false) => {
        setLoading(true);
        const accountCard = accountCardsData.find(
            (card) => card.cardId === selectedAccountCard
        );

        try {
            let result;
            if (isHashavshevetImport) {
                // Hashavshevet import (XLS)
                const hashavshevetResult = await ExternalTransactionsService.PreviewHashavshevetExternalTransactions(
                    client.caseId,
                    selectedAccountCard,
                    accountCard.name,
                    file,
                    token
                );
                result = {
                    transactions: hashavshevetResult.transactions || [],
                    transactionsCount: hashavshevetResult.parsedRows || 0,
                    balanceBefore: 0,
                    balanceAfter: hashavshevetResult.balance || 0,
                    bankBalance: null,
                    systemBalance: 0,
                    totalDebit: hashavshevetResult.totalDebit || 0,
                    totalCredit: hashavshevetResult.totalCredit || 0,
                    isSumitImport: false,
                    isHashavshevetImport: true
                };
            } else if (isSumitImport) {
                // Sumit import - adapt the response to match the expected format
                const sumitResult = await ExternalTransactionsService.PreviewSumitExternalTransactions(
                    client.caseId,
                    selectedAccountCard,
                    accountCard.name,
                    file,
                    token
                );
                
                // Adapt Sumit preview format to match regular bank import format
                result = {
                    transactions: sumitResult.transactions || [],
                    transactionsCount: sumitResult.parsedRows || 0,
                    balanceBefore: 0, // Sumit doesn't provide balance before
                    balanceAfter: sumitResult.balance || 0,
                    bankBalance: null, // Sumit doesn't provide bank balance
                    systemBalance: 0, // Sumit doesn't provide system balance
                    totalDebit: sumitResult.totalDebit || 0,
                    totalCredit: sumitResult.totalCredit || 0,
                    isSumitImport: true, // Mark this as Sumit import
                    isHashavshevetImport: false
                };
            } else {
                // Regular bank import (optionally overridden by Xtram / Isracard template)
                const bankType = useIsracardTemplate
                    ? Constants.BankTypes[102]
                    : useXtramTemplate
                    ? Constants.BankTypes[100]
                    : Constants.BankTypes[parseInt(selectedTBank.bankCode)];
                result = await ExternalTransactionsService.PreviewExternalTransactions(
                    client.caseId,
                    selectedAccountCard,
                    accountCard.name,
                    bankType,
                    file,
                    token
                );
                result.isSumitImport = false;
                result.isHashavshevetImport = false;
                result.isXtramTemplateImport = useXtramTemplate;
                result.isIsracardTemplateImport = useIsracardTemplate;
            }
            setPreviewData(result);
            setPreviewFile(file);
            setOpenPreviewModal(true);
        } catch (error) {
            toast.error(t("BankDataImportFailed"));
            console.error("Error previewing file:", error);
        }
        setLoading(false);
    };

    const handleImport = async (
        file,
        forceSumitImport = null,
        forceHashavshevetImport = null,
        useXtramTemplate = false,
        useIsracardTemplate = false
    ) => {
        setLoading(true);
        const accountCard = accountCardsData.find(
            (card) => card.cardId === selectedAccountCard
        );

        const useSumitImport = forceSumitImport !== null ? forceSumitImport : isSumitImport;
        const useHashavshevetImport = forceHashavshevetImport !== null ? forceHashavshevetImport : isHashavshevetImport;

        try {
            let response;
            if (useHashavshevetImport) {
                // Hashavshevet import (XLS)
                const result = await ExternalTransactionsService.ImportHashavshevetExternalTransactions(
                    client.caseId,
                    selectedAccountCard,
                    accountCard.name,
                    file,
                    token
                );
                if (result && result.processedCount !== undefined) {
                    toast.success(t("BankDataImportSuccessful"));
                    await fetchTransactionsData(endDate);
                } else {
                    toast.error(t("BankDataImportFailed"));
                }
                setLoading(false);
                return;
            } else if (useSumitImport) {
                // Sumit import
                const result = await ExternalTransactionsService.ImportSumitExternalTransactions(
                    client.caseId,
                    selectedAccountCard,
                    accountCard.name,
                    file,
                    token
                );
                if (result && result.processedCount !== undefined) {
                    toast.success(t("BankDataImportSuccessful"));
                    await fetchTransactionsData(endDate);
                } else {
                    toast.error(t("BankDataImportFailed"));
                }
                setLoading(false);
                return;
            } else {
                // Regular bank import (optionally overridden by Xtram / Isracard template)
                const bankType = useIsracardTemplate
                    ? Constants.BankTypes[102]
                    : useXtramTemplate
                    ? Constants.BankTypes[100]
                    : Constants.BankTypes[parseInt(selectedTBank.bankCode)];
                response = await ExternalTransactionsService.ImportExternalTransactions(
                    client.caseId,
                    selectedAccountCard,
                    accountCard.name,
                    bankType,
                    file,
                    token
                );
            }

            if (response && response.ok) {
                toast.success(t("BankDataImportSuccessful"));
                await fetchTransactionsData(endDate);
            } else {
                toast.error(t("BankDataImportFailed"));
            }
        } catch (error) {
            toast.error(t("BankDataImportFailed"));
            console.error("Error importing file:", error);
        }
        setLoading(false);
    };

    const handleConfirmImport = async (options = {}) => {
        if (previewData?.isFinsiteImport) {
            await handleConfirmFinsiteImport(options);
            return;
        }
        if (previewFile) {
            const wasSumitImport = previewData?.isSumitImport ?? isSumitImport;
            const wasHashavshevetImport = previewData?.isHashavshevetImport ?? isHashavshevetImport;
            const wasXtramTemplateImport = previewData?.isXtramTemplateImport ?? false;
            const wasIsracardTemplateImport = previewData?.isIsracardTemplateImport ?? false;
            setOpenPreviewModal(false);
            // Pass flags so the correct import endpoint / bank type is used
            await handleImport(
                previewFile,
                wasSumitImport,
                wasHashavshevetImport,
                wasXtramTemplateImport,
                wasIsracardTemplateImport
            );
            // Reset after import
            setIsSumitImport(false);
            setIsHashavshevetImport(false);
            setPreviewFile(null);
            setPreviewData(null);
        }
    };

    // Balance / Credit / Debit totals for the currently selected transaction rows
    // (same flipped Credit↔Debit display as the top DenseTable).
    const selectedTransactionsSummaryRows = useMemo(() => {
        if (!selectedTransactions.length) return [];

        const selectedIds = new Set(selectedTransactions);
        let debitAccountAmount = 0; // positives → Credit column
        let creditAccountAmount = 0; // negatives → Debit column

        for (const tx of transactions) {
            if (!selectedIds.has(tx.id)) continue;
            debitAccountAmount += Number(tx.debitAccount) || 0;
            creditAccountAmount += Number(tx.creditAccount) || 0;
        }

        return [{
            balance: beautifyNumber(debitAccountAmount - creditAccountAmount),
            credit: beautifyNumber(debitAccountAmount),
            debit: beautifyNumber(creditAccountAmount),
        }];
    }, [selectedTransactions, transactions]);

    const handleCheckboxChange = (transaction) => {
        setSelectedTransactions((prevSelected) => {
            if (prevSelected.includes(transaction.id)) {
                // Remove the transaction if it's already selected
                return prevSelected.filter((id) => id !== transaction.id);
            } else {
                // Add the transaction if it's not selected
                return [...prevSelected, transaction.id];
            }
        });
    }

    const handleSelectAllTransactions = (selectableIds, shouldSelectAll) => {
        setSelectedTransactions(shouldSelectAll ? selectableIds : []);
    };

    const deleteMultipleExternal = async () => {

        if (!selectedTransactions || selectedTransactions.length === 0) {
            console.error("No rows selected for deletion.");
            return;
        }

        const selectedSet = new Set(selectedTransactions.map(String));
        const hasFinsite = (transactions || []).some((tx) => {
            const id = tx?.id ?? tx?.iD ?? tx?.ID;
            if (id == null || !selectedSet.has(String(id))) return false;
            return tx.finsiteEntryId != null || tx.FinsiteEntryId != null;
        });

        const confirmResult = await Swal.fire({
            title: t('confirmDeleteTitle'),
            text: hasFinsite
                ? t('AreYouSureDeleteSelectedFinsite', { count: selectedTransactions.length })
                : t('AreYouSureDeleteSelected', { count: selectedTransactions.length }),
            icon: 'warning',
            showDenyButton: hasFinsite,
            showCancelButton: true,
            confirmButtonColor: '#d32f2f',
            denyButtonColor: '#E57C22',
            cancelButtonColor: '#6c757d',
            confirmButtonText: hasFinsite ? t('DeleteAndBlockFinsite') : t('Delete'),
            denyButtonText: t('DeleteOnly'),
            cancelButtonText: t('Cancel'),
        });

        if (confirmResult.isDismissed) {
            return;
        }

        // Confirm = delete + block Finsite re-import; Deny = delete only (or single-confirm Delete for non-Finsite)
        const blockFinsiteReimport = hasFinsite ? confirmResult.isConfirmed : false;
        if (hasFinsite && !confirmResult.isConfirmed && !confirmResult.isDenied) {
            return;
        }
        if (!hasFinsite && !confirmResult.isConfirmed) {
            return;
        }

        try {
            const response = await ExternalTransactionsService.DeleteMultipleExternalTransactions(
                client.caseId,
                selectedTransactions,
                token,
                blockFinsiteReimport
            );

            if (response.ok) {
                setSelectedTransactions([]);
                await fetchTransactionsData(endDate);
                if (blockFinsiteReimport) {
                    toast.success(t('FinsiteReimportBlocked'));
                }
            } else {
                console.error("Failed to delete transactions:", await response.text());
            }
        } catch (error) {
            console.error("An error occurred while deleting transactions:", error);
        }
    };

    const { hasBack, handleBack } = useBack();

    const handleGoBack = () => {
        // Navigate back to the previous page using React Router
        if (client) {
            // handleBack();
            goBack()
        }
    };
    return (
        <>
            <div className={`bank-workspace ${focusMode ? 'bank-workspace--focus' : ''}`}>
                <div className="bank-workspace__context">
                    <div className={`title-back-container ${direction}`} style={{ marginBottom: '10px', gap: '10px' }}>
                        <Tooltip title={t("DownloadTemplate")} arrow>
                            <Button
                                variant="contained"
                                onClick={() => handleDownloadTemplate(BankFile, "Xtram_Bank_Template.xlsx")}
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    height: "30px",
                                    backgroundColor: "#304FFF",
                                    color: "#fff",
                                    "&:hover": {
                                        backgroundColor: "#1565c0",
                                    },
                                    gap: "10px",
                                    minWidth: "auto", // Ensures button doesn't stretch
                                    // padding: "5px 10px",
                                }}
                            >
                                <DownloadIcon sx={{ width: "20px", height: "20px" }} />
                                {t("DownloadXtramTemplate")}
                            </Button>
                        </Tooltip>
                        <Button
                            variant="contained"
                            onClick={handleImportClick}
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                height: '30px',
                                backgroundColor: !selectedTBank ? "#ccc" : "#304FFF",
                                color: !selectedTBank ? "#777" : "#fff",
                                "&:hover": {
                                    backgroundColor: !selectedTBank
                                        ? "#ccc"
                                        : "#1565c0",
                                },
                                gap: "10px",
                            }}
                        >
                            <CloudUploadIcon />
                            {t("Import")}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSumitImportClick}
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                height: '30px',
                                backgroundColor: !selectedTBank ? "#ccc" : "#304FFF",
                                color: !selectedTBank ? "#777" : "#fff",
                                "&:hover": {
                                    backgroundColor: !selectedTBank
                                        ? "#ccc"
                                        : "#1565c0",
                                },
                                gap: "10px",
                            }}
                        >
                            <CloudUploadIcon />
                            {t("ImportFromSumit")}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleHashavshevetImportClick}
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                height: '30px',
                                backgroundColor: !selectedTBank ? "#ccc" : "#304FFF",
                                color: !selectedTBank ? "#777" : "#fff",
                                "&:hover": {
                                    backgroundColor: !selectedTBank
                                        ? "#ccc"
                                        : "#1565c0",
                                },
                                gap: "10px",
                            }}
                        >
                            <CloudUploadIcon />
                            {t("ImportFromHashavshevet")}
                        </Button>
                        {showFinsiteLinkActions && (
                            <>
                                <Tooltip title={t('FinsiteAutoLinkTooltip')} arrow>
                                    <Button
                                        variant="contained"
                                        onClick={handleFinsiteAutoLink}
                                        disabled={finsiteAutoLinking}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            height: '30px',
                                            backgroundColor: '#1B8A5A',
                                            color: '#fff',
                                            '&:hover': { backgroundColor: '#15724a' },
                                            gap: '10px',
                                        }}
                                    >
                                        {finsiteAutoLinking ? (
                                            <CircularProgress size={16} color="inherit" />
                                        ) : (
                                            <AutoFixHighIcon />
                                        )}
                                        {t('FinsiteAutoLink')}
                                    </Button>
                                </Tooltip>
                                <Tooltip title={t('FinsiteManualLinkTooltip')} arrow>
                                    <Button
                                        variant="contained"
                                        onClick={handleOpenFinsiteLinkDialog}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            height: '30px',
                                            backgroundColor: '#304FFF',
                                            color: '#fff',
                                            '&:hover': { backgroundColor: '#1565c0' },
                                            gap: '10px',
                                        }}
                                    >
                                        <LinkIcon />
                                        {t('FinsiteManualLink')}
                                    </Button>
                                </Tooltip>
                            </>
                        )}
                        {isSelectedAccountFinsiteLinked && (
                            <Tooltip title={t("FinsiteFetchTooltip")} arrow>
                                <Button
                                    variant="contained"
                                    onClick={() => setFinsiteDialogOpen(true)}
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        height: '30px',
                                        backgroundColor: !selectedTBank ? "#ccc" : "#1B8A5A",
                                        color: !selectedTBank ? "#777" : "#fff",
                                        "&:hover": {
                                            backgroundColor: !selectedTBank
                                                ? "#ccc"
                                                : "#15724a",
                                        },
                                        gap: "10px",
                                    }}
                                >
                                    <CloudSyncIcon />
                                    {t("FetchFromFinsite")}
                                </Button>
                            </Tooltip>
                        )}
                        {showFinsiteSignupAction && (
                            <Tooltip title={t('FinsiteSignupRequestTooltip')} arrow>
                                <Button
                                    variant="contained"
                                    onClick={openFinsiteSignupDialog}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        height: '30px',
                                        backgroundColor: '#1B8A5A',
                                        color: '#fff',
                                        '&:hover': { backgroundColor: '#15724a' },
                                        gap: '10px',
                                    }}
                                >
                                    <CloudSyncIcon />
                                    {t('FinsiteSignupRequest')}
                                </Button>
                            </Tooltip>
                        )}
                        {!finsiteHasCredential && finsiteSignupStatusChip && (
                            <Tooltip title={t('FinsiteSignupStatusTooltip')} arrow>
                                <Chip
                                    size="small"
                                    icon={<CloudSyncIcon sx={{ fontSize: '14px !important', color: '#fff !important' }} />}
                                    label={`Finsite: ${finsiteSignupStatusChip.label}`}
                                    sx={{
                                        height: 30,
                                        fontWeight: 600,
                                        color: '#fff',
                                        backgroundColor: finsiteSignupStatusChip.color,
                                        '& .MuiChip-label': { px: 1 },
                                    }}
                                />
                            </Tooltip>
                        )}
                        <Button
                            variant="contained"
                            onClick={handleOpenBankAddTransactionDialog}
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                height: '30px',
                                backgroundColor: !selectedTBank ? "#ccc" : "#304FFF",
                                color: !selectedTBank ? "#777" : "#fff",
                                "&:hover": {
                                    backgroundColor: !selectedTBank
                                        ? "#ccc"
                                        : "#1565c0",
                                },
                                gap: "10px",
                            }}
                        >
                            <AddCircleOutlineIcon style={{ marginRight: "10px" }} />
                            {t("AddBankCommand")}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => setShowDuplicatesModal(true)}
                            disabled={!selectedTBank || !transactions.length}
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                height: '30px',
                                backgroundColor: !selectedTBank || !transactions.length ? "#ccc" : "#E57C22",
                                color: !selectedTBank || !transactions.length ? "#777" : "#fff",
                                "&:hover": {
                                    backgroundColor: !selectedTBank || !transactions.length
                                        ? "#ccc"
                                        : "#c96a1c",
                                },
                                gap: "10px",
                            }}
                        >
                            <ContentCopyIcon />
                            {t("ShowDuplicates")}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleNavigateToBankMatch}
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                height: '30px',
                                backgroundColor: !selectedTBank ? "#ccc" : "#304FFF",
                                color: !selectedTBank ? "#777" : "#fff",
                                "&:hover": {
                                    backgroundColor: !selectedTBank
                                        ? "#ccc"
                                        : "#1565c0",
                                },
                                gap: "10px",
                            }}
                        >
                            {t("NavigateToBankMatches")}
                        </Button>
                    </div>
                {/* Search and Date Filters */}
                <div className={`searchFilter-bar__container ${direction}`}>
                    {/* Search Field */}
                    <TextField
                        style={{ height: '40px', width: '230px' }}
                        placeholder={t('Search')}
                        value={searchQuery}
                        onChange={handleSearchChange}
                        className="search-bar"
                        variant="standard"
                        InputProps={{
                            disableUnderline: true,
                            className: `custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
                            sx: {
                                height: '40px',
                                width: '230px',
                                padding: '0 10px',
                                borderRadius: '8px',
                                background: '#F5F5F6',
                                border: '1px solid transparent',
                                '&:focus-within': {
                                    border: '2px solid var(--Foundation-Blue-Normal, #304FFF)',
                                },
                            },
                            ...(direction === 'ltr'
                                ? {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }
                                : {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }),
                        }}
                        inputProps={{
                            style: {
                                textAlign: direction === 'ltr' ? 'left' : 'right',
                            },
                        }}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showOnlyWithDocuments}
                                onChange={(e) => setShowOnlyWithDocuments(e.target.checked)}
                                size="small"
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#304FFF' },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#304FFF' },
                                }}
                            />
                        }
                        label={
                            <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                                {t('WithDocuments')}
                            </Typography>
                        }
                        sx={{ m: 0, mx: 1 }}
                    />
                    <BankPeriodPicker
                        mode={isRange ? PERIOD_MODE.RANGE : PERIOD_MODE.YEAR}
                        onModeChange={handlePeriodModeChange}
                        year={endDate.getUTCFullYear()}
                        onYearChange={handlePeriodYearChange}
                        startDate={startDate}
                        endDate={endDate}
                        onStartDateChange={handlePeriodStartChange}
                        onEndDateChange={handlePeriodEndChange}
                    />
                </div>
                {/* Which account, its Xtram/Finsite balances, and what the
                    current selection adds up to. Same bar as Bank Matching. */}
                <BankAccountBar
                    selectedAccount={selectedTBank || null}
                    selectedAccountIndex={selectedTBankIndex}
                    onSelectAccount={selectedDenseTableRow}
                    denseTableHeader={denseTableHeader}
                    denseTableRows={denseTableRows}
                    onEditAccount={handleOpenEditTBank}
                    onDeleteSelectedAccounts={handleDeleteSelectedBanks}
                    xtramRows={denseTableCreditDebitRows}
                    finsiteRows={finsiteBalanceRows}
                    diffRows={finsiteDiffRows}
                    showFinsite={isSelectedAccountFinsiteLinked}
                    finsiteLoading={finsiteBalanceLoading}
                    selectedRows={selectedTransactionsSummaryRows}
                    selectedCount={selectedTransactions.length}
                    direction={direction}
                    accountCardSlot={
                        <AutocompleteSelect
                            options={processedCardsData}
                            selectedValues={selectedFormattedAccountCard}
                            onChange={handleFormattedAccountCardChange}
                            label={t("Select AccountCard")}
                            placeholder={t("Select AccountCard")}
                            isMultiple={false}
                            width="100%"
                        />
                    }
                    actionsSlot={
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleOpenCreateModal()}
                            startIcon={<AddCircleOutlineIcon fontSize="small" />}
                            sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                                borderColor: "var(--Foundation-Blue-Normal, #304FFF)",
                                color: "var(--Foundation-Blue-Normal, #304FFF)",
                                "&:hover": {
                                    borderColor: "var(--orange, #F9AA2A)",
                                    color: "var(--orange, #F9AA2A)",
                                },
                            }}
                        >
                            {t("AddBank")}
                        </Button>
                    }
                />
                </div>

                <div className="bank-workspace__toolbar" dir={direction}>
                    <Tooltip title={focusMode ? t('ShowFilters') : t('FocusMode')} arrow>
                        <IconButton
                            onClick={() => setFocusMode(!focusMode)}
                            sx={{
                                backgroundColor: focusMode ? '#F9AA2A' : '#e0e0e0',
                                color: focusMode ? '#fff' : '#304FFF',
                                width: 36,
                                height: 36,
                                '&:hover': { backgroundColor: focusMode ? '#E6951F' : '#d5d5d5' },
                            }}
                        >
                            {focusMode ? <UnfoldMoreIcon fontSize="small" /> : <UnfoldLessIcon fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                    <span className="bank-workspace__summary-chip">
                        {`${formatUtcDate(displayDateRange.start)} — ${formatUtcDate(displayDateRange.end)}`}
                    </span>
                    <Tooltip title={t('DownloadExcel')} arrow>
                        <span>
                            <Button
                                variant="outlined"
                                size="small"
                                disabled={!filteredTransactions.length}
                                onClick={handleDownloadTransactionsExcel}
                                startIcon={<DownloadIcon fontSize="small" />}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                    height: 28,
                                    borderColor: 'var(--Foundation-Blue-Normal, #304FFF)',
                                    color: 'var(--Foundation-Blue-Normal, #304FFF)',
                                    '&.Mui-disabled': {
                                        borderColor: '#ccc',
                                        color: '#777',
                                    },
                                    '&:hover': {
                                        borderColor: 'var(--orange, #F9AA2A)',
                                        color: 'var(--orange, #F9AA2A)',
                                    },
                                }}
                            >
                                {t('DownloadExcel')}
                            </Button>
                        </span>
                    </Tooltip>
                    <div className="bank-workspace__toolbar-spacer" />
                </div>

                <div className="bank-workspace__body">
                    <TransactionsTable
                        transactions={filteredTransactions}
                        handleEdit={handleEdit}
                        formatDate={formatDate}
                        handleSortChange={handleSortChange}
                        sortConfig={sortConfig}
                        handleCheckboxChange={handleCheckboxChange}
                        selectedTransactions={selectedTransactions}
                        setSelectedTransactions={setSelectedTransactions}
                        handleSelectAll={handleSelectAllTransactions}
                        handleDeleteExternalRows={deleteMultipleExternal}
                        handleViewTransactionMatch={handleViewTransactionMatch}
                    />
                </div>
            </div>

            <ViewTransactionMatchModal
                open={isTransactionMatchModalOpen}
                onClose={handleCloseTransactionMatchModal}
                match={selectedTransactionMatch}
                loading={isLoadingTransactionMatch}
                formatDate={formatDate}
                formatAccountLabel={formatAccountLabel}
                isForeignCurrency={selectedTBank?.foreignCurrencyCode != null}
                onRemoveMatch={handleRemoveMatchClick}
                isRemovingMatch={isRemovingMatch}
                t={t}
                direction={direction}
            />

            <DuplicateEntriesModal
                open={showDuplicatesModal}
                onClose={() => setShowDuplicatesModal(false)}
                rows={bankDuplicateRows}
                t={t}
                direction={direction}
                loading={loading}
                getGroupKey={bankDuplicateGetGroupKey}
                shouldSelectForDeletion={bankShouldSelectForDeletion}
                subtitle={t('BankDuplicateEntriesSubtitle')}
                enableSmartMatching
                columns={[
                    {
                        key: 'documentDate',
                        label: t('date') || t('DocumentDate') || 'Date',
                        render: (r) => normalizeDate(r.documentDate),
                    },
                    {
                        key: 'reference',
                        label: t('Reference') || 'Reference',
                        render: (r) => r.reference || '',
                    },
                    {
                        key: 'amount',
                        label: t('Amount') || 'Amount',
                        render: (r) => {
                            const n = Number(r.amount ?? 0);
                            if (isNaN(n)) return r.amount;
                            return n.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            });
                        },
                    },
                    {
                        key: 'description',
                        label: t('Description') || 'Description',
                        render: (r) => r.description || '',
                    },
                    {
                        key: 'matchNumber',
                        label: t('MatchNumber') || 'Match',
                        render: (r) =>
                            r.matchNumber == null || r.matchNumber === -1 || r.matchNumber === 0
                                ? '-'
                                : r.matchNumber,
                    },
                ]}
                onDeleteMany={async (ids) => {
                    if (!ids?.length || !client?.caseId) return;

                    const idSet = new Set(ids.map(String));
                    const hasFinsite = (transactions || []).some((tx) => {
                        const id = tx?.id ?? tx?.iD ?? tx?.ID;
                        if (id == null || !idSet.has(String(id))) return false;
                        return tx.finsiteEntryId != null || tx.FinsiteEntryId != null;
                    });

                    let blockFinsiteReimport = false;
                    if (hasFinsite) {
                        const confirmResult = await Swal.fire({
                            title: t('confirmDeleteTitle'),
                            text: t('DeleteFinsiteBlockPrompt'),
                            icon: 'warning',
                            showDenyButton: true,
                            showCancelButton: true,
                            confirmButtonColor: '#d32f2f',
                            denyButtonColor: '#E57C22',
                            cancelButtonColor: '#6c757d',
                            confirmButtonText: t('DeleteAndBlockFinsite'),
                            denyButtonText: t('DeleteOnly'),
                            cancelButtonText: t('Cancel'),
                        });
                        if (confirmResult.isDismissed) return;
                        blockFinsiteReimport = confirmResult.isConfirmed;
                        if (!confirmResult.isConfirmed && !confirmResult.isDenied) return;
                    }

                    setLoading(true);
                    try {
                        const response = await ExternalTransactionsService.DeleteMultipleExternalTransactions(
                            client.caseId,
                            ids,
                            token,
                            blockFinsiteReimport
                        );
                        if (response.ok) {
                            toast.success(
                                blockFinsiteReimport
                                    ? t('FinsiteReimportBlocked')
                                    : t('DeletedSuccessfully')
                            );
                            setSelectedTransactions([]);
                            await fetchTransactionsData(endDate);
                        } else {
                            toast.error(t('FailedToDeleteTransaction'));
                        }
                    } catch (err) {
                        console.error(err);
                        toast.error(t('FailedToDeleteTransaction'));
                    }
                    setLoading(false);
                }}
            />

            {selectedMatchForRemoval && (
                <Dialog
                    open={openRemoveMatchDialog}
                    onClose={handleCloseRemoveMatchDialog}
                    aria-labelledby="confirm-remove-match-title"
                    dir={direction}
                    aria-describedby="confirm-remove-match-description"
                    sx={{
                        '& .MuiDialog-paper': {
                            backgroundColor: 'white',
                            color: 'black',
                            borderRadius: '8px',
                            padding: '20px',
                        },
                    }}
                >
                    <DialogTitle
                        id="confirm-remove-match-title"
                        sx={{ fontSize: '20px', color: '#E57C22' }}
                    >
                        {t('ConfirmRemoval')}
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText
                            id="confirm-remove-match-description"
                            sx={{ color: 'black', fontSize: '16px' }}
                        >
                            {t('ConfirmRemoveMatch')}
                            <br />
                            <strong>{t('CaseID')}:</strong> {selectedMatchForRemoval.caseId}
                            <br />
                            <strong>{t('AccountCard Name')}:</strong> {selectedMatchForRemoval.accountCardName}
                            <br />
                            <strong>{t('AccountCodeID')}:</strong> {selectedMatchForRemoval.accountCodeId}
                            <br />
                            <strong>{t('TransactionDate')}:</strong>{' '}
                            {selectedMatchForRemoval.transactionDate
                                ? formatDate(selectedMatchForRemoval.transactionDate)
                                : '-'}
                            <br />
                            <strong>{t('MatchDate')}:</strong>{' '}
                            {selectedMatchForRemoval.matchDate
                                ? formatDate(selectedMatchForRemoval.matchDate)
                                : '-'}
                            <br />
                            <strong>{t('MatchNumber')}:</strong> {selectedMatchForRemoval.matchNumber}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'center', padding: '16px' }}>
                        <Button
                            onClick={handleCloseRemoveMatchDialog}
                            disabled={isRemovingMatch}
                            sx={{ color: '#E57C22', fontWeight: 'bold' }}
                        >
                            {t('Cancel')}
                        </Button>
                        <Button
                            onClick={handleConfirmRemoveMatch}
                            disabled={isRemovingMatch}
                            sx={{
                                backgroundColor: '#E57C22',
                                color: 'white',
                                fontWeight: 'bold',
                                padding: '8px 16px',
                                borderRadius: '4px',
                                '&:hover': {
                                    backgroundColor: '#d46d1e',
                                },
                            }}
                            autoFocus
                        >
                            {isRemovingMatch ? t('Loading') || '...' : t('Confirm')}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
            <Dialog
                open={finsiteDialogOpen}
                onClose={() => setFinsiteDialogOpen(false)}
                dir={direction}
                maxWidth="xs"
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        backgroundColor: 'white',
                        color: 'black',
                        borderRadius: '8px',
                        padding: '8px',
                    },
                }}
            >
                <DialogTitle sx={{ fontSize: '20px', color: '#1B8A5A' }}>
                    {t('FetchFromFinsite')}
                </DialogTitle>
                <DialogContent>
                    <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                        <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label={t('FinsiteFromDate')}
                            value={finsiteFrom}
                            onChange={(e) => setFinsiteFrom(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label={t('FinsiteToDate')}
                            value={finsiteTo}
                            onChange={(e) => setFinsiteTo(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </div>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', padding: '16px' }}>
                    <Button
                        onClick={() => setFinsiteDialogOpen(false)}
                        disabled={loading}
                        sx={{ color: '#1B8A5A', fontWeight: 'bold' }}
                    >
                        {t('Cancel')}
                    </Button>
                    <Button
                        onClick={handleFinsiteFetch}
                        disabled={loading}
                        startIcon={<CloudSyncIcon />}
                        sx={{
                            backgroundColor: '#1B8A5A',
                            color: 'white',
                            fontWeight: 'bold',
                            padding: '8px 16px',
                            borderRadius: '4px',
                            '&:hover': { backgroundColor: '#15724a' },
                        }}
                    >
                        {loading ? t('Loading') || '...' : t('Preview')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={finsiteSignupDialogOpen}
                onClose={() => setFinsiteSignupDialogOpen(false)}
                dir={direction}
                maxWidth="sm"
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        backgroundColor: 'white',
                        color: 'black',
                        borderRadius: '8px',
                        padding: '8px',
                    },
                }}
            >
                <DialogTitle sx={{ fontSize: '20px', color: '#1B8A5A' }}>
                    {t('FinsiteSignupRequest')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ fontSize: '14px', marginBottom: '12px' }}>
                        {t('FinsiteSignupDialogIntro')}
                    </DialogContentText>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '4px' }}>
                        <TextField
                            fullWidth
                            size="small"
                            label={t('Name')}
                            value={finsiteSignupForm.accountantName}
                            onChange={(e) => setFinsiteSignupForm((p) => ({ ...p, accountantName: e.target.value }))}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            label={t('AccountantId')}
                            value={finsiteSignupForm.accountantId}
                            InputProps={{ readOnly: true }}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            label={t('Email')}
                            value={finsiteSignupForm.email}
                            onChange={(e) => setFinsiteSignupForm((p) => ({ ...p, email: e.target.value }))}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            label={t('PhoneNumber')}
                            value={finsiteSignupForm.phoneNumber}
                            onChange={(e) => setFinsiteSignupForm((p) => ({ ...p, phoneNumber: e.target.value }))}
                        />
                    </div>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', padding: '16px' }}>
                    <Button
                        onClick={() => setFinsiteSignupDialogOpen(false)}
                        disabled={finsiteSignupSending}
                        sx={{ color: '#1B8A5A', fontWeight: 'bold' }}
                    >
                        {t('Cancel')}
                    </Button>
                    <Button
                        onClick={handleSendFinsiteSignupRequest}
                        disabled={finsiteSignupSending}
                        startIcon={
                            finsiteSignupSending
                                ? <CircularProgress size={16} color="inherit" />
                                : <CloudSyncIcon />
                        }
                        sx={{
                            backgroundColor: '#1B8A5A',
                            color: 'white',
                            fontWeight: 'bold',
                            padding: '8px 16px',
                            borderRadius: '4px',
                            '&:hover': { backgroundColor: '#15724a' },
                        }}
                    >
                        {t('FinsiteSignupSend')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={finsiteLinkDialogOpen}
                onClose={() => setFinsiteLinkDialogOpen(false)}
                dir={direction}
                maxWidth="md"
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        backgroundColor: 'white',
                        color: 'black',
                        borderRadius: '8px',
                        padding: '8px',
                    },
                }}
            >
                <DialogTitle sx={{ fontSize: '20px', color: '#304FFF' }}>
                    {t('FinsiteManualLink')}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {client?.name} ({client?.caseId}) · {selectedTBank?.bankName} {selectedTBank?.accountNumber}
                    </Typography>
                    {finsiteDiscovering ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                            <Autocomplete
                                size="small"
                                options={finsiteCompanies}
                                getOptionLabel={(o) => `${o.name || ''} (${o.taxRegistrationNumber || ''})`}
                                isOptionEqualToValue={(o, v) => String(o.entryId) === String(v.entryId)}
                                value={
                                    finsiteCompanies.find(
                                        (c) => String(c.entryId) === String(finsiteLinkForm.companyEntryId)
                                    ) || null
                                }
                                onChange={(e, val) => setFinsiteLinkForm({
                                    companyEntryId: val?.entryId ?? '',
                                    accountId: '',
                                    bookingAccountId: '',
                                })}
                                renderInput={(params) => (
                                    <TextField {...params} label={t('FinsiteCompany')} />
                                )}
                            />
                            <Autocomplete
                                size="small"
                                options={filteredFinsiteAccounts}
                                getOptionLabel={(o) => `${o.bank || ''} ${o.branch || ''}/${o.accountNumber || ''}`.trim()}
                                isOptionEqualToValue={(o, v) => String(o.entryId) === String(v.entryId)}
                                value={
                                    filteredFinsiteAccounts.find(
                                        (a) => String(a.entryId) === String(finsiteLinkForm.accountId)
                                    ) || null
                                }
                                onChange={(e, val) => setFinsiteLinkForm((p) => ({
                                    ...p,
                                    accountId: val?.entryId ?? '',
                                    bookingAccountId: '',
                                }))}
                                disabled={!finsiteLinkForm.companyEntryId}
                                renderInput={(params) => (
                                    <TextField {...params} label={t('FinsiteAccount')} />
                                )}
                            />
                            <Autocomplete
                                size="small"
                                options={filteredFinsiteBooking}
                                getOptionLabel={(o) => {
                                    const base = formatFinsiteBookingLabel(o);
                                    const linked = finsiteMappedBookingAccountIds.includes(String(o.entryId));
                                    return linked ? `${base} (${t('FinsiteLinked')})` : base;
                                }}
                                isOptionEqualToValue={(o, v) => String(o.entryId) === String(v.entryId)}
                                value={
                                    filteredFinsiteBooking.find(
                                        (b) => String(b.entryId) === String(finsiteLinkForm.bookingAccountId)
                                    ) || null
                                }
                                onChange={(e, val) => setFinsiteLinkForm((p) => ({
                                    ...p,
                                    bookingAccountId: val?.entryId ?? '',
                                }))}
                                disabled={!finsiteLinkForm.accountId}
                                renderOption={(props, option) => {
                                    const linked = finsiteMappedBookingAccountIds.includes(String(option.entryId));
                                    const label = formatFinsiteBookingLabel(option);
                                    return (
                                        <li {...props} key={option.entryId}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                                                <span>{label}</span>
                                                {linked && (
                                                    <Chip
                                                        size="small"
                                                        label={t('FinsiteLinked')}
                                                        color="success"
                                                        sx={{ height: 20, fontSize: 11 }}
                                                    />
                                                )}
                                            </Box>
                                        </li>
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label={t('FinsiteBookingAccount')} />
                                )}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'space-between', padding: '16px' }}>
                    <Button
                        onClick={() => setFinsiteLinkDialogOpen(false)}
                        disabled={finsiteSavingMapping}
                    >
                        {t('Cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleFinsiteCreateMapping}
                        disabled={finsiteSavingMapping || finsiteDiscovering}
                        startIcon={finsiteSavingMapping ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />}
                        sx={{
                            backgroundColor: '#304FFF',
                            '&:hover': { backgroundColor: '#1565c0' },
                        }}
                    >
                        {t('FinsiteSaveLink')}
                    </Button>
                </DialogActions>
            </Dialog>
            <ToastContainer />
            {/* {client && <HamburgerMenu client={client} />} */}
            {loading && (
                <div className="loader-container">
                    <PuffLoader size={100} loading={loading} />
                </div>
            )}
            {/* Import Dialog */}
            {openImportDialog && (
                <ImportDialog
                    open={openImportDialog}
                    onClose={() => {
                        setOpenImportDialog(false);
                        setIsSumitImport(false);
                    }}
                    onImport={handleImport}
                    onPreview={handlePreview}
                    showXtramTemplateOption={true}
                    showIsracardTemplateOption={true}
                />
            )}
            {/* Sumit Import Dialog */}
            {openSumitImportDialog && (
                <ImportDialog
                    open={openSumitImportDialog}
                    onClose={() => {
                        setOpenSumitImportDialog(false);
                        setIsSumitImport(false);
                    }}
                    onImport={handleImport}
                    onPreview={handlePreview}
                />
            )}
            {openHashavshevetImportDialog && (
                <ImportDialog
                    open={openHashavshevetImportDialog}
                    onClose={() => {
                        setOpenHashavshevetImportDialog(false);
                        setIsHashavshevetImport(false);
                    }}
                    onImport={handleImport}
                    onPreview={handlePreview}
                />
            )}
            <ImportPreviewModal
                open={openPreviewModal}
                onClose={() => {
                    setOpenPreviewModal(false);
                    setPreviewData(null);
                    setPreviewFile(null);
                    // Don't reset isSumitImport here - let handleConfirmImport handle it
                }}
                onConfirm={handleConfirmImport}
                previewData={previewData}
                loading={loading}
            />
            {/* Add External Transaction Dialog */}
            {/* {openAddTransactionDialog && (
                <AddExternalTransactionDialog
                    open={openAddTransactionDialog}
                    onClose={handleCloseAddTransactionDialog}
                    client={client}
                    token={token}
                    accountCardOptions={accountCardOptions}
                    onAddSuccess={() => fetchTransactionsData(endDate)}
                    selectedTBank={selectedTBank}
                />
            )} */}

            <EditExternalTransactionDialog
                editBankTransactionDialog={editBankTransactionDialog}
                handleCloseBankEditTransactionDialog={handleCloseBankEditTransactionDialog}
                t={t}
                editedTransaction={editedTransaction}
                changeNewBankTransaction={changeEditedBankTransaction}
                handleEditBankTransaction={handleEditBankTransaction}
                handleDelete={handleDelete}
                selectedTBank={selectedTBank}
                token={token}
                viewOnly={editViewOnly}
            />

            <AddExternalTransactionDialog
                addBankTransactionDialog={addBankTransactionDialog}
                handleCloseBankAddTransactionDialog={handleCloseBankAddTransactionDialog}
                t={t}
                newBankEditableItem={newBankEditableItem}
                changeNewBankTransaction={changeNewBankTransaction}
                handleAddBankTransaction={handleAddBankTransaction}
                selectedTBank={selectedTBank}
            />

            {/* AddOrEditBankDialog */}
            {createBankModal && (
                <AddOrEditBankDialog
                    open={createBankModal}
                    onClose={handleCloseCreateModal}
                    isEditingBank={isEditingBank}
                    selectedBankForCreate={selectedBankForCreate}
                    bankOptions={bankOptions} // Add actual bank options
                    handleChangeBankForCreate={handleChangeBankForCreate}
                    bankBranch={bankBranch}
                    handleChangeCreateBank={handleChangeCreateBank}
                    foreignCurrencyCode={foreignCurrencyCode}
                    bankAccountNumber={bankAccountNumber}
                    cardForNewBank={cardForNewBank}
                    processedCardsData={accountCardOptionsForBank}
                    setCardForNewBank={handleChangeSetCardForNewBank}
                    handleAddOrEditBank={handleAddOrEditBank}
                    handleRemoveBank={handleRemoveBank}
                    client={client}
                    token={token}
                    setLoading={setLoading}
                    palCodes={formattedPalCodeOptions}
                    fetchEnums={fetchEnums}
                />
            )}
        </>
    );
}

export default BankPages;
